import {
    Component,
    ViewChild,
    AfterViewInit,
    ChangeDetectorRef,
    HostListener,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    TemplateRef,
    afterEveryRender,
    effect,
    inject,
    signal,
    untracked,
    ChangeDetectionStrategy,
} from '@angular/core';
import { alert as dxAlert } from 'devextreme/ui/dialog';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
    get as _get,
    map as _map,
    filter as _filter,
    chunk as _chunk,
    each as _each,
    escape as _escape,
    every as _every,
    find as _find,
    trim as _trim,
    flatten as _flatten,
    groupBy as _groupBy,
    includes as _includes,
    isEmpty as _isEmpty,
    isNil as _isNil,
    isString as _isString,
    keyBy as _keyBy,
    orderBy as _orderBy,
    padStart as _padStart,
    reduce as _reduce,
    partition as _partition,
    some as _some,
    sumBy as _sumBy,
    take as _take,
    toString as _toString,
    uniq as _uniq,
    uniqBy as _uniqBy,
    values as _values,
} from 'lodash';
import {
    defer,
    forkJoin,
    from,
    Observable,
    of,
    Subject,
    throwError,
    timer,
} from 'rxjs';
import {
    catchError,
    concatMap,
    map,
    mergeMap,
    retryWhen,
    switchMap,
    takeUntil,
    takeWhile,
    toArray,
} from 'rxjs/operators';
import moment from 'moment';
import { formatBuddhistEra } from 'src/app/shared/pipes/thai-datetime.pipe';
import { DxDataGridComponent } from 'devextreme-angular';
import {
    HeaderContentService,
    InvoiceRequestService,
    LastPrintPlatform,
    LastPrintResult,
    LastPrintTimeService,
    LazadaOpenService,
    LexDocService,
    MessageType,
    NotificationService,
    ShopeeOpenServiceV2,
    StoreRegistryService,
    TiktokOpenService,
    UnifiedQueryV2Service,
    UtilService,
} from 'src/app/shared/services';
import { InvoiceRequest } from 'src/app/shared/services/invoice-request/interfaces/api';
import {
    COLLECTOR_PLATFORMS,
    PLATFORM_AGNOSTIC,
} from 'src/app/shared/services/invoice-request/platform-map';
import { environment } from 'src/environments/environment';
import { PrintSheetService } from 'src/app/shared/services/print-sheet/print-sheet.service';
import { InvoiceParameter } from 'src/app/shared/services/print-sheet/interfaces/invoice-param';
import { ShopeeShippingLabelParameter } from 'src/app/shared/services/print-sheet/interfaces/shopee-shipping-label-param';
import { ShopeeShippingDocDataInfo } from 'src/app/shared/services/shopee/interfaces/shopee-shipping-doc-data';
import { OrderStatus as LazadaOrderStatus } from 'src/app/shared/services/lazada/enums/order-status';
import { OrderStatus as ShopeeOrderStatus } from 'src/app/shared/services/shopee/enums/order-status-v2';
import { OrderStatus as TiktokOrderStatus } from 'src/app/shared/services/tiktok/enums/order-status';
import { PrintSheetUtils } from 'src/app/shared/services/print-sheet/utils';
import {
    LazadaFormatDateTimePipe,
    ShopeeFormatDateTimePipe,
} from 'src/app/shared/pipes';
import {
    StoreConfig,
    StoreId,
    StorePrintInfo,
    sellerFor,
    storeById,
    taxInvoiceEntityIdFor,
} from '../../config/store-config';
import {
    entityById,
    EntityId,
    vatRegisteredEntityById,
} from '../../config/entity-config';
import { OutputTaxService } from '../../services/output-tax/tax-output.service';
import { RenderPersistReportService } from '../../services/print-sheet/render-persist-report.service';
import { TaxInvoicePrintSheetParameter } from '../../services/print-sheet/interfaces/tax-invoice-param';
import {
    TaxInvoiceIssueRequest,
    TaxInvoiceIssueResult,
    TaxInvoiceIssueService,
    TaxInvoiceIssueTarget,
} from '../../services/tax-invoice-issue/tax-invoice-issue.service';
import { TaxInvoiceBuyer } from '../../services/tax-invoice-issue/build-tax-invoice';
import { ReceiptInfo, ReceiptOrder } from './receipt-order';
import {
    ReceiptDetail,
    UnifiedOrder,
    UnifiedOrderItem,
    UnifiedOrderStatus,
    UNIFIED_STATUS_MAPPING,
} from './unified-order';
import { ReceiptDetailComponent } from '../receipt-detail/receipt-detail.component';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import {
    DxiColumnComponent,
    DxiToolbarItemComponent,
    DxoFilterRowComponent,
    DxoLoadPanelComponent,
    DxoSelectionComponent,
    DxoPagingComponent,
    DxoPagerComponent,
    DxoSummaryComponent,
    DxiTotalItemComponent,
} from 'devextreme-angular/ui/nested';
import { DxLoadPanelComponent } from 'devextreme-angular/ui/load-panel';
import { DxTemplateDirective } from 'devextreme-angular/core';
import { DxTabsComponent } from 'devextreme-angular/ui/tabs';
import { DxTooltipComponent } from 'devextreme-angular/ui/tooltip';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import { DxDropDownButtonComponent } from 'devextreme-angular/ui/drop-down-button';
import {
    AWAITING_LOOKBACK_DAYS,
    ReturnOrdersService,
} from '../../services/returns/return-orders.service';
import { ReturnPackage } from '../../services/returns/return-package';
import { DxDataGridComponent as DxDataGridComponent_1 } from 'devextreme-angular/ui/data-grid';
import { FormatNumberPipe } from '../../pipes/format-number.pipe';
import { FormatRelativeDatetimePipe } from '../../pipes/format-relative-datetime.pipe';
import { ReceiptInfoPopupComponent } from '../receipt-info-popup/receipt-info-popup.component';
import {
    HEADER_STAGE_CLASSES,
    IN_PAGE_STAGE,
    fitMenuStage,
    oneLineWidths,
} from './header-menu-fit';

type HandoverMethod = 'PICKUP' | 'DROP_OFF';

interface StoreContext {
    store: StoreConfig;
    injector: Injector;
    fetchOrders: (status: UnifiedOrderStatus) => Observable<UnifiedOrder[]>;
}

interface OrderStatusTab {
    key: UnifiedOrderStatus;
    text: string;
}

// An invoice ready to render, paired with the billing request it was issued
// from (when there was one) so that record can be marked issued once the
// render succeeds.
interface InvoicePrintTarget {
    param: InvoiceParameter;
    billingRequest?: InvoiceRequest;
}

// One order through the customer-resolution pipeline: the plain invoice param
// with the billing request / recognizer receipt already applied, plus the
// sources themselves so the tax path can build the buyer block from them.
interface ResolvedInvoiceOrder extends InvoicePrintTarget {
    order: UnifiedOrder;
    receiptOrder?: ReceiptOrder;
}

@Component({
    selector: 'app-combined-orders',
    templateUrl: './combined-orders.component.html',
    styleUrls: ['./combined-orders.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        DxButtonComponent,
        DxTemplateDirective,
        DxTabsComponent,
        DxDataGridComponent_1,
        DxLoadPanelComponent,
        DxiColumnComponent,
        DxoFilterRowComponent,
        DxoLoadPanelComponent,
        DxoSelectionComponent,
        DxoPagingComponent,
        DxoPagerComponent,
        DxoSummaryComponent,
        DxiTotalItemComponent,
        DxiToolbarItemComponent,
        DxTooltipComponent,
        DxPopupComponent,
        ReceiptDetailComponent,
        ReceiptInfoPopupComponent,
        DxDropDownButtonComponent,
        FormatNumberPipe,
        FormatRelativeDatetimePipe,
        DatePipe,
        NgTemplateOutlet,
    ],
})
export class CombinedOrdersComponent
    implements AfterViewInit, OnChanges, OnDestroy
{
    // Background re-query cadence and cutoff for orders still flagged
    // "No tax receipt or recognized customer info" on the Ready to Ship tab.
    private static readonly RECEIPT_POLL_INTERVAL_MS = 5 * 1000;
    private static readonly RECEIPT_POLL_TIME_LIMIT_MS = 5 * 60 * 1000;

    // Cap on how many order numbers a lookup-failure toast spells out.
    private static readonly MAX_ORDERS_LISTED_IN_MESSAGE = 5;

    // Order numbers per batched billing-request lookup. The API caps a batch
    // at 100 and that is also the page limit, so one batch is one page as
    // long as an order number doesn't recur across stores. Console order
    // numbers are short (Shopee ~14 chars, Lazada/TikTok numeric), so the
    // full cap stays well under URL length limits.
    private static readonly BILLING_LOOKUP_BATCH_SIZE = 100;

    // Backoff retries per batch on a throttled billing lookup.
    private static readonly BILLING_LOOKUP_RETRIES = 2;

    // In-flight complete -> issued transitions after a print.
    private static readonly MARK_ISSUED_CONCURRENCY = 4;

    // In-flight server-side renders while archiving issued tax invoices —
    // each is a Lambda→Lambda jsreport render plus a Drive upload.
    private static readonly ARCHIVE_CONCURRENCY = 2;

    private cdref = inject(ChangeDetectorRef);
    private injector = inject(Injector);
    private notificationService = inject(NotificationService);
    private utilService = inject(UtilService);
    private lazadaFormatDateTimePipe = inject(LazadaFormatDateTimePipe);
    private shopeeFormatDateTimePipe = inject(ShopeeFormatDateTimePipe);
    private printSheetService = inject(PrintSheetService);
    private lastprintTimeService = inject(LastPrintTimeService);
    private unifiedQueryV2Service = inject(UnifiedQueryV2Service);
    private invoiceRequestService = inject(InvoiceRequestService);
    private storeRegistry = inject(StoreRegistryService);
    private taxInvoiceIssueService = inject(TaxInvoiceIssueService);
    private outputTaxService = inject(OutputTaxService);
    private renderPersistService = inject(RenderPersistReportService);
    private headerContentService = inject(HeaderContentService);
    private returnOrdersService = inject(ReturnOrdersService);

    // Return requests the platforms are holding for the shop's approve /
    // reject, shown as a notice in the header's centre slot. Only requests
    // — what the shop must decide — never the receipt confirmations, which
    // are the return-packages page's business.
    pendingReturnRequests: ReturnPackage[] = [];
    pendingReturnMenuItems: { text: string; row: ReturnPackage }[] = [];
    pendingReturnFailedStores: string[] = [];
    readonly pendingReturnDropDownOptions = { width: 560, maxHeight: '70vh' };

    @ViewChild('dataGrid', { static: true })
    private dataGrid!: DxDataGridComponent;
    @ViewChild('receiptInfoPopup')
    private receiptInfoPopup?: ReceiptInfoPopupComponent;
    // Action buttons, notices and counts: in the app header's center slot
    // while they fit there on one line, else at the top of the page.
    @ViewChild('headerMenu', { static: true })
    private headerMenu!: TemplateRef<unknown>;

    // Where the menu renders and how shortened (header-menu-fit.ts). Starts
    // in the page, where the first measurement is taken.
    readonly menuStage = signal(IN_PAGE_STAGE);
    readonly inPageStage = IN_PAGE_STAGE;
    private menuWidths: number[] = [];
    // The rendered menu, wherever it is, and the observer that re-measures
    // it whenever its content changes.
    private menuElement: HTMLElement | null = null;
    private readonly menuObserver = new MutationObserver(() =>
        this.measureMenu(),
    );
    // The grid's box changes height with no window resize when the menu
    // moves between header and page or re-wraps in the page, and DevExtreme
    // re-lays the grid out on window resize only — without this the pager
    // ends up clipped below the card, or a gap opens under it.
    private gridBoxHeight = 0;
    private readonly gridBoxObserver = new ResizeObserver(([entry]) => {
        const height = Math.round(entry.contentRect.height);
        if (height === this.gridBoxHeight) {
            return;
        }
        const firstReading = this.gridBoxHeight === 0;
        this.gridBoxHeight = height;
        if (!firstReading) {
            this.dataGrid.instance.updateDimensions();
            // Then the same debounced page-size fit as a window resize.
            this.onWindowResize();
        }
    });
    @Input()
    stores: StoreConfig[] = [];

    isDataLoading = false;
    readonly loadPanelPosition = { of: '#combinedOrderGrid' };
    hasRemarks = false;
    dropOffOnlyStoreTitles: string[] = [];
    detailPopupVisible = false;
    detailPopupTitle = '';
    detailPopupText = '';
    // Set when the popup is showing receipt details rather than a remark.
    detailPopupReceipt: ReceiptDetail | null = null;
    selectedRowKeys: string[] = [];
    deferSelectedOrderStatus: UnifiedOrderStatus = UnifiedOrderStatus.ToShip;
    orderSource: UnifiedOrder[] = [];
    orderStatuses: OrderStatusTab[] = [
        {
            key: UnifiedOrderStatus.ToShip,
            text: 'To Ship',
        },
        {
            key: UnifiedOrderStatus.ReadyToShip,
            text: 'Ready to Ship',
        },
        {
            key: UnifiedOrderStatus.Shipped,
            text: 'Shipped',
        },
        {
            key: UnifiedOrderStatus.Completed,
            text: 'Completed',
        },
        {
            key: UnifiedOrderStatus.Cancelled,
            text: 'Cancelled',
        },
        {
            key: UnifiedOrderStatus.Unpaid,
            text: 'Unpaid',
        },
    ];
    selectedTabItem: OrderStatusTab | undefined;
    copyDetailButtonOptions = {
        text: 'Copy',
        icon: 'copy',
        onClick: (): void => {
            navigator.clipboard.writeText(this.detailPopupText).then(
                () => {
                    this.notifySuccess('Copied to clipboard');
                },
                (error) => {
                    this.notifyError(error);
                },
            );
        },
    };

    private storeContexts: StoreContext[] = [];
    private storeContextsByTitle = new Map<string, StoreContext>();
    private viewInitialized = false;
    private updateOrderSubject = new Subject<UnifiedOrderStatus | undefined>();
    // The billing request resolved for each order while the receipt indicators
    // were built, keyed by order. Handed to the receipt popup so opening it
    // doesn't re-ask the collector for a record we already picked — and so the
    // popup edits the same record the print will be issued from.
    private billingRequestsByOrderKey = new Map<string, InvoiceRequest>();

    // storeIds the collector will actually accept a record under. Loaded once;
    // a store missing from it cannot have receipt info recorded, so the menu
    // says so rather than letting the save fail with STORE_UNKNOWN.
    private registeredStoreIds = new Set<string>();

    private destroy$ = new Subject<void>();
    private resizeTimer?: ReturnType<typeof setTimeout>;

    constructor() {
        effect(() => {
            if (this.menuStage() === IN_PAGE_STAGE) {
                this.headerContentService.clear(this.headerMenu);
            } else {
                this.headerContentService.set(this.headerMenu);
            }
        });
        // The widths do not depend on the slot, so a slot that resizes
        // (window, side nav) re-places the menu without re-measuring it.
        effect(() => {
            const slotWidth = this.headerContentService.slotWidth();
            untracked(() => this.placeMenu(slotWidth));
        });
        afterEveryRender(() => this.trackMenuElement());
    }

    get menuStageClasses(): string {
        return (HEADER_STAGE_CLASSES[this.menuStage()] ?? []).join(' ');
    }

    get canShip(): boolean {
        return this.deferSelectedOrderStatus === UnifiedOrderStatus.ToShip;
    }

    get canPrint(): boolean {
        return this.deferSelectedOrderStatus === UnifiedOrderStatus.ReadyToShip;
    }

    get showLastPrint(): boolean {
        return (
            this.deferSelectedOrderStatus === UnifiedOrderStatus.ReadyToShip ||
            this.deferSelectedOrderStatus === UnifiedOrderStatus.Shipped
        );
    }

    get showTracking(): boolean {
        return (
            this.deferSelectedOrderStatus === UnifiedOrderStatus.ReadyToShip ||
            this.deferSelectedOrderStatus === UnifiedOrderStatus.Shipped ||
            this.deferSelectedOrderStatus === UnifiedOrderStatus.Completed
        );
    }

    get showCarrier(): boolean {
        return (
            this.deferSelectedOrderStatus === UnifiedOrderStatus.ToShip ||
            this.showTracking
        );
    }

    get selectionMode(): string {
        return this.canShip || this.canPrint ? 'multiple' : 'none';
    }

    ngOnChanges(): void {
        this.storeContexts = _map(this.stores, (store: StoreConfig) =>
            this.createStoreContext(store),
        );
        this.storeContextsByTitle = new Map(
            _map(
                this.storeContexts,
                (context: StoreContext): [string, StoreContext] => [
                    context.store.title,
                    context,
                ],
            ),
        );
        if (this.viewInitialized) {
            this.updateOrderSubject.next(undefined);
        }
    }

    ngAfterViewInit(): void {
        this.gridBoxObserver.observe(
            this.dataGrid.instance.element() as unknown as HTMLElement,
        );
        this.loadPendingReturnRequests();
        if (!this.selectedTabItem) {
            this.selectedTabItem = this.getTabByStatus(
                UnifiedOrderStatus.ToShip,
            );
            this.cdref.detectChanges();
        }
        this.updateOrderSubject
            .pipe(
                switchMap((orderStatus: UnifiedOrderStatus | undefined) =>
                    this.loadOrders(orderStatus),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe();
        this.storeRegistry
            .knownIds()
            .pipe(takeUntil(this.destroy$))
            .subscribe((ids: Set<string>) => {
                this.registeredStoreIds = ids;
            });
        this.viewInitialized = true;
        this.updateOrderSubject.next(undefined);
    }

    // After every render, because the menu's element is replaced whenever it
    // moves between header and page, and whenever DevExtreme re-renders the
    // header toolbar's items. A no-op unless the element changed.
    private trackMenuElement(): void {
        const menu = document.querySelector<HTMLElement>(
            '.combined-orders-menu',
        );
        if (menu === this.menuElement) {
            return;
        }
        this.menuObserver.disconnect();
        this.menuElement = menu;
        if (menu) {
            this.menuObserver.observe(menu, {
                childList: true,
                subtree: true,
                characterData: true,
            });
            this.measureMenu();
        }
    }

    private measureMenu(): void {
        if (this.menuElement?.isConnected) {
            this.menuWidths = oneLineWidths(this.menuElement);
            this.placeMenu();
        }
    }

    private placeMenu(
        slotWidth: number = this.headerContentService.slotWidth(),
    ): void {
        this.menuStage.set(
            fitMenuStage(this.menuWidths, slotWidth, this.menuStage()),
        );
    }

    refreshOrders(): void {
        this.updateOrderSubject.next(undefined);
        // The refresh button is also how the operator asks whether a new
        // return request has come in.
        this.loadPendingReturnRequests(true);
    }

    // ---- Return requests awaiting the shop's decision ----

    get pendingReturnHint(): string {
        const base = `คำขอคืนสินค้าที่แพลตฟอร์มรอให้ร้านตัดสินใจ (${AWAITING_LOOKBACK_DAYS} วันล่าสุด) — ตัดสินใจที่ Seller Center ของแพลตฟอร์ม คลิกรายการเพื่อคัดลอกเลขคำสั่งซื้อ`;
        return this.pendingReturnFailedStores.length
            ? `${base} · ดึงข้อมูลไม่ได้: ${this.pendingReturnFailedStores.join(', ')}`
            : base;
    }

    // The decision itself is made in the platform's seller centre, and until
    // it is made there is no package for the returns page to track — so a
    // click only hands over the order number for pasting into that search.
    onPendingReturnClick(event: any): void {
        const row: ReturnPackage | undefined = event?.itemData?.row;
        if (!row) {
            return;
        }
        navigator.clipboard?.writeText(row.orderNo).then(
            () =>
                this.notificationService.pushMessage({
                    message: `คัดลอกเลขคำสั่งซื้อ ${row.orderNo} แล้ว — ไปตัดสินใจคำขอคืนที่ Seller Center ของ ${row.storeTitle}`,
                    messageType: MessageType.info,
                }),
            () =>
                this.notificationService.pushMessage({
                    message: `เลขคำสั่งซื้อ ${row.orderNo} (${row.storeTitle})`,
                    messageType: MessageType.info,
                }),
        );
    }

    // Deliberately fire-and-forget and never blocking the orders load: the
    // notice is a hint, and a platform that cannot be asked just drops out
    // of the count (named in the hint).
    private loadPendingReturnRequests(force = false): void {
        this.returnOrdersService
            .fetchAwaitingDecision(force)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({ rows, failed }) => {
                    this.pendingReturnRequests = rows;
                    this.pendingReturnFailedStores = failed;
                    this.pendingReturnMenuItems = rows.map((row) => ({
                        text: this.pendingReturnItemText(row),
                        row,
                    }));
                    this.cdref.markForCheck();
                },
                error: (error: unknown) =>
                    console.warn('return requests lookup failed', error),
            });
    }

    private pendingReturnItemText(row: ReturnPackage): string {
        const items = row.items
            .map((item) =>
                item.qty > 1 ? `${item.sku} ×${item.qty}` : item.sku,
            )
            .join(', ');
        const when = row.createdAt ? row.createdAt.fromNow() : '';
        return [row.storeTitle, row.orderNo, items, row.reason, when]
            .filter(Boolean)
            .join(' · ');
    }

    // Header badge counts, mirroring the grid's order-id indicators: green =
    // receipt/recognized customer info available, red = the customer asked
    // for a full tax invoice. Getters so the popup edits and the background
    // poll are picked up without bookkeeping; only populated on the
    // pre-shipment tabs where the indicators are fetched.
    get receiptAvailableCount(): number {
        return _sumBy(this.orderSource, (order: UnifiedOrder) =>
            order.receiptAvailable && !order.receiptDetail?.taxInvoiceRequested
                ? 1
                : 0,
        );
    }

    get taxInvoiceRequestedCount(): number {
        return _sumBy(this.orderSource, (order: UnifiedOrder) =>
            order.receiptDetail?.taxInvoiceRequested ? 1 : 0,
        );
    }

    ngOnDestroy(): void {
        this.headerContentService.clear(this.headerMenu);
        this.menuObserver.disconnect();
        this.gridBoxObserver.disconnect();
        if (this.resizeTimer) {
            clearTimeout(this.resizeTimer);
        }
        this.destroy$.next();
        this.destroy$.complete();
        this.updateOrderSubject.complete();
    }

    // Loads the selected tab in two phases: render the core orders as soon as
    // they arrive, then enrich (last print, Shopee tracking, receipt
    // indicators). The loading indicator spans both phases — one show here,
    // one hide when the last enrichment lands — so it doesn't blink between
    // phases; only the open-ended receipt poll runs after it clears.
    private loadOrders(
        orderStatus: UnifiedOrderStatus | undefined,
    ): Observable<unknown> {
        this.dataGridBeginLoading();
        if (!orderStatus) {
            orderStatus = this.selectedTabItem!.key;
            this.cdref.detectChanges();
        }
        const status = orderStatus;
        if (_isEmpty(this.storeContexts)) {
            this.renderOrders([], status);
            this.dataGridEndLoading();
            return of(null);
        }
        return this.fetchCoreOrders(status).pipe(
            switchMap((orders: UnifiedOrder[]) => {
                this.renderOrders(orders, status);
                return this.enrichOrders(orders, status);
            }),
            catchError((error) => {
                // Recover so a failed load doesn't kill the pipeline and the
                // next tab selection still works.
                this.notifyError(error);
                this.dataGridEndLoading();
                return of(null);
            }),
        );
    }

    private fetchCoreOrders(
        status: UnifiedOrderStatus,
    ): Observable<UnifiedOrder[]> {
        return forkJoin(
            _map(this.storeContexts, (context: StoreContext) =>
                context.fetchOrders(status).pipe(
                    catchError((error) => {
                        this.notifyError(error, context.store.title);
                        return of([] as UnifiedOrder[]);
                    }),
                ),
            ),
        ).pipe(
            map((results: UnifiedOrder[][]) =>
                _orderBy(_flatten(results), ['createDate'], ['desc']),
            ),
        );
    }

    private renderOrders(
        orders: UnifiedOrder[],
        status: UnifiedOrderStatus,
    ): void {
        this.hasRemarks = _some(orders, (order: UnifiedOrder) =>
            _trim(order.remarks),
        );
        this.dropOffOnlyStoreTitles = _uniq(
            _map(
                _filter(orders, (order: UnifiedOrder) =>
                    this.isDropOffOnly(order),
                ),
                'storeTitle',
            ),
        );
        this.deferSelectedOrderStatus = status;
        this.orderSource = orders;
        this.selectedRowKeys = [];
        this.selectRowsByTab();
        this.cdref.detectChanges();
    }

    // Fills the columns not needed for the first render — last print, Shopee
    // tracking numbers, receipt indicators — patching the orders in place and
    // re-applying the per-tab selection once last-print data is known.
    private enrichOrders(
        orders: UnifiedOrder[],
        status: UnifiedOrderStatus,
    ): Observable<unknown> {
        const tasks: Observable<unknown>[] = [];
        if (
            status === UnifiedOrderStatus.ReadyToShip ||
            status === UnifiedOrderStatus.Shipped
        ) {
            tasks.push(
                this.fetchLastPrints().pipe(
                    map((lastPrints: LastPrintResult[]) => {
                        this.fillLastPrintData(orders, lastPrints);
                        return null;
                    }),
                    catchError(() => of(null)),
                ),
            );
        }
        if (
            status === UnifiedOrderStatus.ReadyToShip ||
            status === UnifiedOrderStatus.Shipped ||
            status === UnifiedOrderStatus.Completed
        ) {
            tasks.push(this.fetchShopeeTracking(orders));
        }
        if (
            status === UnifiedOrderStatus.ToShip ||
            status === UnifiedOrderStatus.ReadyToShip
        ) {
            tasks.push(
                this.attachReceiptIndicators(orders, status).pipe(
                    map(() => null),
                ),
            );
        }
        if (_isEmpty(tasks)) {
            this.dataGridEndLoading();
            return of(null);
        }
        return forkJoin(tasks).pipe(
            map(() => {
                // Reflect the patched columns and apply the Ready to Ship
                // unprinted-row selection now that last-print data is known.
                this.selectRowsByTab(true);
                this.dataGridEndLoading();
                return null;
            }),
            switchMap(() =>
                status === UnifiedOrderStatus.ReadyToShip
                    ? this.pollMissingReceiptInfo(orders)
                    : of(null),
            ),
        );
    }

    // The recognizer job fills receipt_orders asynchronously, so a Ready to
    // Ship order can show the missing-receipt warning only because its
    // document hasn't arrived yet. Keep re-querying the flagged orders in the
    // background until every warning clears or the time limit passes; a tab
    // change, refresh, or reload cancels the loop through loadOrders'
    // switchMap.
    private pollMissingReceiptInfo(
        orders: UnifiedOrder[],
    ): Observable<unknown> {
        if (!_some(orders, 'missingReceiptData')) {
            return of(null);
        }
        const deadline =
            Date.now() + CombinedOrdersComponent.RECEIPT_POLL_TIME_LIMIT_MS;
        return timer(
            CombinedOrdersComponent.RECEIPT_POLL_INTERVAL_MS,
            CombinedOrdersComponent.RECEIPT_POLL_INTERVAL_MS,
        ).pipe(
            takeWhile(
                () =>
                    Date.now() < deadline &&
                    _some(orders, 'missingReceiptData'),
            ),
            concatMap(() => {
                const missingOrders = _filter(orders, 'missingReceiptData');
                return this.attachReceiptIndicators(
                    missingOrders,
                    UnifiedOrderStatus.ReadyToShip,
                    false,
                ).pipe(
                    map(() => {
                        // Repaint only when a warning actually cleared so the
                        // grid isn't refreshed on every empty poll.
                        if (
                            _some(
                                missingOrders,
                                (order: UnifiedOrder) =>
                                    !order.missingReceiptData,
                            )
                        ) {
                            this.dataGrid.instance.refresh();
                        }
                        return null;
                    }),
                );
            }),
        );
    }

    // Fetches Shopee tracking numbers per store (Shopee returns them one
    // request per order) off the critical path and patches them onto the orders.
    private fetchShopeeTracking(orders: UnifiedOrder[]): Observable<unknown> {
        const shopeeByStore = _groupBy(
            _filter(
                orders,
                (order: UnifiedOrder) => order.platform === 'shopee',
            ),
            'storeTitle',
        );
        const tasks = _map(
            shopeeByStore,
            (storeOrders: UnifiedOrder[], storeTitle: string) => {
                const context = this.storeContextsByTitle.get(storeTitle);
                if (!context) {
                    return of(null);
                }
                const service = context.injector.get(ShopeeOpenServiceV2);
                return service
                    .getTrackingNumbers(_map(storeOrders, 'orderId'))
                    .pipe(
                        map((trackingNumbers: any[]) => {
                            const trackingByOrderSn = _keyBy(
                                trackingNumbers,
                                'orderSn',
                            );
                            _each(storeOrders, (order: UnifiedOrder) => {
                                order.trackingNumber = _toString(
                                    _get(
                                        trackingByOrderSn[order.orderId],
                                        'trackingNumber',
                                    ) || '',
                                );
                            });
                            return null;
                        }),
                        catchError(() => of(null)),
                    );
            },
        );
        return _isEmpty(tasks) ? of(null) : forkJoin(tasks);
    }

    private selectRowsByTab(applyReadyToShipSelection = false): void {
        this.dataGrid.instance.refresh();
        this.dataGrid.instance.clearSelection();
        if (this.deferSelectedOrderStatus === UnifiedOrderStatus.ToShip) {
            // Select via the selectedRowKeys binding rather than
            // instance.selectAll(): the latter runs against the grid's
            // previous dataSource because the new one is only pushed in
            // by the next change-detection pass.
            this.selectedRowKeys = _map(this.orderSource, 'key');
        } else if (
            applyReadyToShipSelection &&
            this.deferSelectedOrderStatus === UnifiedOrderStatus.ReadyToShip
        ) {
            // Only auto-select unprinted rows once last-print data has loaded;
            // before then every row looks unprinted, so select nothing.
            this.selectedRowKeys = _map(
                _filter(
                    this.orderSource,
                    (order: UnifiedOrder) => !order.lastPrintMoment,
                ),
                'key',
            );
        }
    }

    onGridContentReady(): void {
        this.adjustPageSizeToHeight();
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        // Debounce: resize fires rapidly, and each measurement forces layout.
        if (this.resizeTimer) {
            clearTimeout(this.resizeTimer);
        }
        this.resizeTimer = setTimeout(() => this.adjustPageSizeToHeight(), 150);
    }

    // Sizes the page to the number of rows that fit the grid's row area so the
    // table fills the screen without its own scrollbar. Stable because the
    // measured height doesn't change when the page size does.
    private adjustPageSizeToHeight(): void {
        const instance = this.dataGrid?.instance;
        if (!instance) {
            return;
        }
        const root = instance.element() as unknown as HTMLElement;
        const rowsView = root.querySelector(
            '.dx-datagrid-rowsview',
        ) as HTMLElement | null;
        const dataRow = rowsView?.querySelector(
            '.dx-data-row',
        ) as HTMLElement | null;
        const rowHeight = dataRow?.offsetHeight ?? 0;
        if (!rowsView || !rowHeight) {
            return;
        }
        const fittingRows = Math.max(
            1,
            Math.floor(rowsView.clientHeight / rowHeight),
        );
        if (fittingRows !== instance.pageSize()) {
            instance.option('paging.pageSize', fittingRows);
        }
    }

    showRemarkPopup(order: UnifiedOrder): void {
        this.detailPopupTitle = `Remark — ${order.orderId}`;
        // The popup is shared with the receipt view; clearing this puts it
        // back to plain text.
        this.detailPopupReceipt = null;
        this.detailPopupText = order.remarks;
        this.detailPopupVisible = true;
    }

    // Right-click a row on the pre-shipment tabs to record the customer's
    // billing details for that order. The grid defines no context menu of its
    // own, so `items` may be absent.
    onContextMenuPreparing(e: {
        target?: string;
        row?: { rowType?: string; data?: UnifiedOrder };
        items?: {
            text: string;
            icon?: string;
            disabled?: boolean;
            onItemClick: () => void;
        }[];
    }): void {
        if (
            e.target !== 'content' ||
            e.row?.rowType !== 'data' ||
            !e.row.data ||
            !this.isPreShipmentTab()
        ) {
            return;
        }
        const order = e.row.data;
        // The collector rejects a record whose store it doesn't know, so say
        // that up front instead of failing once the form has been filled in.
        const registered = this.registeredStoreIds.has(order.storeId);
        e.items = e.items || [];
        e.items.push({
            text: !registered
                ? `Receipt info unavailable — ${order.storeTitle} is not a registered store`
                : order.receiptAvailable
                  ? 'Edit receipt info…'
                  : 'Fill receipt info…',
            icon: 'edit',
            disabled: !registered,
            onItemClick: () =>
                this.receiptInfoPopup?.open(
                    order,
                    this.billingRequestsByOrderKey.get(order.key),
                ),
        });
    }

    // Only worth offering before the invoice is printed. Derived from the same
    // deferred status as every other tab-gated flag, so it can't disagree with
    // the rows actually on screen during a load.
    private isPreShipmentTab(): boolean {
        return this.canShip || this.canPrint;
    }

    // The popup hands back the record it just persisted, so the row's indicator
    // is updated from that directly — re-reading it from the collector would
    // only confirm what we already know.
    onReceiptInfoSaved(saved: {
        order: UnifiedOrder;
        request: InvoiceRequest;
    }) {
        const { order, request } = saved;
        this.billingRequestsByOrderKey.set(order.key, request);
        if (!this.isPreShipmentTab()) {
            return;
        }
        order.receiptAvailable = true;
        order.receiptDetail = this.billingReceiptDetail(request);
        order.missingReceiptData = false;
        const rowIndex = this.dataGrid.instance.getRowIndexByKey(order.key);
        if (rowIndex >= 0) {
            this.dataGrid.instance.repaintRows([rowIndex]);
        }
        this.cdref.detectChanges();
    }

    showReceiptPopup(order: UnifiedOrder, event?: Event): void {
        // The icon sits inside the row, so the click would otherwise reach the
        // grid and toggle that row's selection along with opening the popup.
        event?.stopPropagation();
        this.detailPopupTitle = `Tax receipt — ${order.orderId}`;
        this.detailPopupReceipt = order.receiptDetail ?? null;
        // Copy still works off plain text.
        this.detailPopupText = this.detailPopupReceipt
            ? this.receiptDetailToText(this.detailPopupReceipt)
            : 'Full receipt available';
        this.detailPopupVisible = true;
    }

    orderStatusSelectionChanged(event: any): void {
        const orderStatusKey: UnifiedOrderStatus = _get(
            event,
            'addedItems[0].key',
        );
        this.updateOrderSubject.next(orderStatusKey);
    }

    private createStoreContext(store: StoreConfig): StoreContext {
        switch (store.platform) {
            case 'lazada':
                return this.buildStoreContext(
                    store,
                    'lazadaAuthInfo',
                    [LazadaOpenService, LexDocService],
                    (injector: Injector, status: UnifiedOrderStatus) =>
                        this.fetchLazadaOrders(
                            injector.get(LazadaOpenService),
                            store,
                            UNIFIED_STATUS_MAPPING[status].lazada,
                        ),
                );
            case 'shopee':
                return this.buildStoreContext(
                    store,
                    'ssmAuthInfo',
                    [ShopeeOpenServiceV2],
                    (injector: Injector, status: UnifiedOrderStatus) =>
                        this.fetchShopeeOrders(
                            injector.get(ShopeeOpenServiceV2),
                            store,
                            UNIFIED_STATUS_MAPPING[status].shopee,
                        ),
                );
            case 'tiktok':
                return this.buildStoreContext(
                    store,
                    'tiktokAuthInfo',
                    [TiktokOpenService],
                    (injector: Injector, status: UnifiedOrderStatus) =>
                        this.fetchTiktokOrders(
                            injector.get(TiktokOpenService),
                            store,
                            UNIFIED_STATUS_MAPPING[status].tiktok,
                        ),
                );
        }
    }

    // Builds a store-scoped injector so each store's service instances see
    // that store's authInfo through the platform's string DI token.
    private buildStoreContext(
        store: StoreConfig,
        authInfoToken: string,
        serviceClasses: any[],
        fetchOrders: (
            injector: Injector,
            status: UnifiedOrderStatus,
        ) => Observable<UnifiedOrder[]>,
    ): StoreContext {
        const injector = Injector.create({
            providers: [
                { provide: authInfoToken, useValue: store.authInfo },
                ...serviceClasses,
            ],
            parent: this.injector,
        });
        return {
            store,
            injector,
            fetchOrders: (status: UnifiedOrderStatus) =>
                fetchOrders(injector, status),
        };
    }

    private fetchLazadaOrders(
        service: LazadaOpenService,
        store: StoreConfig,
        orderStatus: LazadaOrderStatus,
    ): Observable<UnifiedOrder[]> {
        return service.getOrderByStatus(orderStatus).pipe(
            switchMap((response: any) => {
                const orders: any[] = _get(response, 'orders') || [];
                const orderIds: number[] = _map(orders, 'order_id');
                return forkJoin([of(orders), service.getOrderItems(orderIds)]);
            }),
            map(([orders, orderItemList]: [any[], any[]]) => {
                return _map(orders, (order: any): UnifiedOrder => {
                    const orderItemObj = _find(orderItemList, {
                        order_id: order.order_id, // eslint-disable-line @typescript-eslint/naming-convention
                    });
                    const orderItems: any[] =
                        _get(orderItemObj, 'order_items') || [];
                    order.orderItems = orderItems;
                    const createMoment =
                        this.lazadaFormatDateTimePipe.tryParseToMoment(
                            order.created_at,
                        );
                    return {
                        key: `${store.title}:${order.order_id}`,
                        storeTitle: store.title,
                        storeId: store.storeId,
                        platform: 'lazada',
                        orderId: _toString(order.order_id),
                        matching: this.toMatchingNumber(order.order_id),
                        createDate: moment.isMoment(createMoment)
                            ? createMoment.toDate()
                            : null,
                        status:
                            _toString(_get(order, 'statuses[0]')) ||
                            orderStatus,
                        recipientName: `${
                            _get(order, 'address_shipping.first_name') || ''
                        } ${
                            _get(order, 'address_shipping.last_name') || ''
                        }`.trim(),
                        paymentMethod: _toString(order.payment_method),
                        totalAmount: this.utilService.localStringToNumber(
                            order.price,
                        ),
                        itemsCount: orderItems.length,
                        trackingNumber: _toString(
                            _get(orderItems, '[0].tracking_code') || '',
                        ),
                        shippingCarrier: _toString(
                            _get(orderItems, '[0].shipment_provider') || '',
                        ),
                        remarks: _toString(order.remarks || ''),
                        raw: order,
                        items: this.groupUnitItems(
                            orderItems,
                            'sku',
                            (item: any) => item.name,
                            (item: any) => item.item_price,
                            (item: any) =>
                                _includes(item.variation, 'เป็นกลาง')
                                    ? ''
                                    : item.variation,
                        ),
                    };
                });
            }),
        );
    }

    private fetchShopeeOrders(
        service: ShopeeOpenServiceV2,
        store: StoreConfig,
        orderStatus: ShopeeOrderStatus,
    ): Observable<UnifiedOrder[]> {
        return service
            .getOrderByStatusIncludingDetails(
                moment().subtract(14, 'days'),
                moment(),
                orderStatus,
            )
            .pipe(
                // Tracking numbers are fetched separately during background
                // enrichment (Shopee returns them one request per order) so
                // they don't block the initial render.
                map((orders: any[]) =>
                    _map(
                        _filter(orders, (order: any) => !!order),
                        (order: any): UnifiedOrder => {
                            let itemNo = 0;
                            const items: UnifiedOrderItem[] = _map(
                                order.item_list || [],
                                (item: any): UnifiedOrderItem => {
                                    const quantity =
                                        this.utilService.localStringToNumber(
                                            item.model_quantity_purchased,
                                        );
                                    const unitPrice =
                                        this.utilService.localStringToNumber(
                                            item.model_discounted_price,
                                        );
                                    return {
                                        itemNo: ++itemNo,
                                        description:
                                            PrintSheetUtils.constructItemDescription(
                                                item.item_sku,
                                                item.item_name,
                                                quantity,
                                                item.model_name,
                                            ),
                                        quantity,
                                        unitPrice,
                                        amount: unitPrice * quantity,
                                    };
                                },
                            );
                            return {
                                key: `${store.title}:${order.order_sn}`,
                                storeTitle: store.title,
                                storeId: store.storeId,
                                platform: 'shopee',
                                orderId: _toString(order.order_sn),
                                matching: this.toMatchingNumber(order.order_sn),
                                createDate: order.create_time
                                    ? moment.unix(order.create_time).toDate()
                                    : null,
                                status:
                                    _toString(order.order_status) ||
                                    orderStatus,
                                recipientName: _toString(
                                    _get(order, 'recipient_address.name') || '',
                                ),
                                paymentMethod: _toString(order.payment_method),
                                totalAmount:
                                    this.utilService.localStringToNumber(
                                        order.total_amount,
                                    ),
                                itemsCount: _sumBy(items, 'quantity'),
                                trackingNumber: '',
                                shippingCarrier: _toString(
                                    _get(order, 'shipping_carrier') || '',
                                ),
                                remarks: _toString(
                                    order.message_to_seller || '',
                                ),
                                items,
                                raw: order,
                            };
                        },
                    ),
                ),
            );
    }

    private fetchTiktokOrders(
        service: TiktokOpenService,
        store: StoreConfig,
        orderStatus: TiktokOrderStatus,
    ): Observable<UnifiedOrder[]> {
        return service.getOrderList(orderStatus).pipe(
            map((response: any) => {
                const orders: any[] = _get(response, 'orders') || [];
                return _map(orders, (order: any): UnifiedOrder => {
                    const lineItems: any[] = order.line_items || [];
                    return {
                        key: `${store.title}:${order.id}`,
                        storeTitle: store.title,
                        storeId: store.storeId,
                        platform: 'tiktok',
                        orderId: _toString(order.id),
                        matching: this.toMatchingNumber(order.id),
                        createDate: order.create_time
                            ? moment.unix(order.create_time).toDate()
                            : null,
                        status: _toString(order.status) || orderStatus,
                        recipientName: this.shortenAsteriskMask(
                            _toString(
                                _get(order, 'recipient_address.name') || '',
                            ),
                        ),
                        paymentMethod: _toString(order.payment_method_name),
                        totalAmount: this.utilService.localStringToNumber(
                            _get(order, 'payment.total_amount'),
                        ),
                        itemsCount: lineItems.length,
                        trackingNumber: _toString(order.tracking_number || ''),
                        shippingCarrier: _toString(
                            _get(lineItems, '[0].shipping_provider_name') ||
                                order.shipping_provider ||
                                '',
                        ),
                        remarks: _toString(order.buyer_message || ''),
                        raw: order,
                        items: this.groupUnitItems(
                            lineItems,
                            'seller_sku',
                            (item: any) => item.product_name,
                            (item: any) => item.sale_price,
                            (item: any) => item.sku_name,
                        ),
                    };
                });
            }),
        );
    }

    shipSelectedOrders(handoverMethod: HandoverMethod): void {
        const selectedOrders = this.getSelectedRowsData();
        if (_isEmpty(selectedOrders)) {
            return;
        }
        this.dataGridBeginLoading();
        const shipObservables = _map(selectedOrders, (order: UnifiedOrder) =>
            this.shipOrder(order, handoverMethod).pipe(
                map(() => true),
                catchError((error) => {
                    this.notifyError(
                        error,
                        `${order.storeTitle} ${order.orderId}`,
                    );
                    return of(false);
                }),
            ),
        );
        forkJoin(shipObservables).subscribe((results: boolean[]) => {
            const successfulOrders = _filter(
                selectedOrders,
                (_order: UnifiedOrder, index: number) => results[index],
            );
            if (_isEmpty(successfulOrders)) {
                this.dataGridEndLoading();
                return;
            }
            this.notifySuccess(
                this.buildShipSuccessMessage(successfulOrders, handoverMethod),
            );
            // Jump to Ready to Ship so the orders can be printed right
            // away. Assigning selectedTabItem alone doesn't reliably
            // fire dx-tabs' selection-changed event from inside this
            // callback, so push the change and refresh explicitly. The
            // loading indicator stays up through the reload, which ends it.
            this.selectedTabItem = this.getTabByStatus(
                UnifiedOrderStatus.ReadyToShip,
            );
            this.cdref.detectChanges();
            this.updateOrderSubject.next(UnifiedOrderStatus.ReadyToShip);
        });
    }

    // On a Pickup request, dropOffOnly Shopee stores are redirected to drop
    // off, so report the two outcomes separately instead of claiming every
    // order was a pickup.
    private buildShipSuccessMessage(
        successfulOrders: UnifiedOrder[],
        handoverMethod: HandoverMethod,
    ): string {
        if (handoverMethod === 'DROP_OFF') {
            return `Drop off arranged for ${successfulOrders.length} order(s)`;
        }
        const droppedOff = _filter(successfulOrders, (order: UnifiedOrder) =>
            this.isDropOffOnly(order),
        ).length;
        const pickedUp = successfulOrders.length - droppedOff;
        const parts: string[] = [];
        if (pickedUp > 0) {
            parts.push(`Pickup scheduled for ${pickedUp} order(s)`);
        }
        if (droppedOff > 0) {
            parts.push(`Drop off arranged for ${droppedOff} order(s)`);
        }
        return parts.join('; ');
    }

    private shipOrder(
        order: UnifiedOrder,
        handoverMethod: HandoverMethod,
    ): Observable<unknown> {
        let injector: Injector;
        try {
            injector = this.getStoreInjector(order);
        } catch (error) {
            return throwError(error);
        }
        switch (order.platform) {
            case 'lazada':
                // Lazada has no per-order pickup/drop-off API; the handover
                // method is a seller-account setting and courier scheduling
                // happens automatically once the order is set Ready to Ship.
                return this.shipLazadaOrder(
                    injector.get(LazadaOpenService),
                    order,
                );
            case 'shopee':
                // Stores flagged dropOffOnly cannot schedule pickups, so
                // Schedule Pickup arranges a drop off for their orders.
                return handoverMethod === 'PICKUP' && !this.isDropOffOnly(order)
                    ? this.shipShopeeOrderPickup(
                          injector.get(ShopeeOpenServiceV2),
                          order,
                      )
                    : this.shipShopeeOrderDropoff(
                          injector.get(ShopeeOpenServiceV2),
                          order,
                      );
            case 'tiktok':
                return this.shipTiktokOrder(
                    injector.get(TiktokOpenService),
                    order,
                    handoverMethod,
                );
        }
    }

    private isDropOffOnly(order: UnifiedOrder): boolean {
        const store = this.storeContextsByTitle.get(order.storeTitle)?.store;
        return store?.platform === 'shopee' && !!store.dropOffOnly;
    }

    private getStoreContext(order: UnifiedOrder): StoreContext {
        const context = this.storeContextsByTitle.get(order.storeTitle);
        if (!context) {
            throw new Error(`No store context for ${order.storeTitle}`);
        }
        return context;
    }

    private getStoreInjector(order: UnifiedOrder): Injector {
        return this.getStoreContext(order).injector;
    }

    // What this order's store prints as the seller and as the sender. Orders
    // only ever come from a configured store's own fetch, so the lookup cannot
    // miss for an order on the grid.
    private getPrintInfo(order: UnifiedOrder): StorePrintInfo {
        return this.getStoreContext(order).store.printInfo;
    }

    private shipLazadaOrder(
        service: LazadaOpenService,
        order: UnifiedOrder,
    ): Observable<unknown> {
        const orderItemIds: number[] = _map(
            _get(order.raw, 'orderItems') || [],
            'order_item_id',
        );
        if (_isEmpty(orderItemIds)) {
            return throwError(
                new Error(`No order items found for order ${order.orderId}`),
            );
        }
        return service.setStatusToPackedByMarketplace(orderItemIds).pipe(
            switchMap((response) => {
                const responseOrderItems: any[] =
                    _get(response, 'order_items') || [];
                const trackGroups = _groupBy(
                    responseOrderItems,
                    'tracking_number',
                );
                const rtsObservables: Observable<unknown>[] = _map(
                    trackGroups,
                    (objList: any[], trackingNumber: string) =>
                        service.setStatusToReadyToShip(
                            _map(objList, 'order_item_id'),
                            _get(objList[0], 'shipment_provider'),
                            trackingNumber,
                        ),
                );
                return _isEmpty(rtsObservables)
                    ? of([])
                    : forkJoin(rtsObservables);
            }),
        );
    }

    private shipShopeeOrderPickup(
        service: ShopeeOpenServiceV2,
        order: UnifiedOrder,
    ): Observable<unknown> {
        return service.getShippingParam(order.orderId).pipe(
            switchMap((shippingParam: any) => {
                const addressList: any[] =
                    _get(shippingParam, 'pickup.address_list') || [];
                const pickupAddress =
                    _find(addressList, (addressObj: any) =>
                        _includes(addressObj.address_flag, 'pickup_address'),
                    ) || addressList[0];
                const addressId: number = _get(pickupAddress, 'address_id');
                const pickupTimeId: string = _get(
                    pickupAddress,
                    'time_slot_list[0].pickup_time_id',
                );
                if (!addressId || !pickupTimeId) {
                    throw new Error(
                        `No pickup address or time slot available for order ${order.orderId}`,
                    );
                }
                return service.shipOrder(
                    order.orderId,
                    addressId,
                    pickupTimeId,
                );
            }),
        );
    }

    private shipShopeeOrderDropoff(
        service: ShopeeOpenServiceV2,
        order: UnifiedOrder,
    ): Observable<unknown> {
        return service.getShippingParam(order.orderId).pipe(
            switchMap((shippingParam: any) => {
                const branchId: number = _get(
                    shippingParam,
                    'dropoff.branch_list[0].branch_id',
                );
                return service.shipOrderDropoff(order.orderId, branchId);
            }),
        );
    }

    private shipTiktokOrder(
        service: TiktokOpenService,
        order: UnifiedOrder,
        handoverMethod: HandoverMethod,
    ): Observable<unknown> {
        const packageIds: string[] = _map(
            _get(order.raw, 'packages') || [],
            'id',
        );
        if (_isEmpty(packageIds)) {
            return throwError(
                new Error(`No packages found for order ${order.orderId}`),
            );
        }
        return forkJoin(
            _map(packageIds, (packageId: string) =>
                service.shipPackage(packageId, handoverMethod),
            ),
        );
    }

    private getSelectedRowsData(): UnifiedOrder[] {
        const selectedRowKeys = this.dataGrid.instance.getSelectedRowKeys();
        return _filter(this.orderSource, (order: UnifiedOrder) =>
            _includes(selectedRowKeys, order.key),
        );
    }

    printInvoices(): void {
        this.runPrintTaskForSelection(
            (orders: UnifiedOrder[]) => this.requestInvoicePrinting(orders),
            (count: number) => `Printed invoices for ${count} order(s)`,
        );
    }

    printShippingLabels(): void {
        this.runPrintTaskForSelection(
            (orders: UnifiedOrder[]) =>
                this.requestShippingLabelPrinting(orders),
            (count: number) => `Printed shipping labels for ${count} order(s)`,
        );
    }

    printBoth(): void {
        this.runPrintTaskForSelection(
            (orders: UnifiedOrder[]) =>
                forkJoin([
                    this.requestInvoicePrinting(orders),
                    this.requestShippingLabelPrinting(orders, false),
                ]),
            (count: number) =>
                `Printed invoices and shipping labels for ${count} order(s)`,
        );
    }

    private runPrintTaskForSelection(
        buildTask: (orders: UnifiedOrder[]) => Observable<unknown>,
        buildSuccessMessage: (orderCount: number) => string,
    ): void {
        // Sort by store (asc) then creation time (desc) before printing.
        // Store titles are platform-prefixed, so this keeps each platform's
        // orders contiguous; the per-platform grouping in the invoice and
        // label flows then preserves this order and keeps the two stacks
        // paired sheet by sheet.
        const selectedOrders = _orderBy(
            this.getSelectedRowsData(),
            ['storeTitle', 'createDate'],
            ['asc', 'desc'],
        );
        if (_isEmpty(selectedOrders)) {
            return;
        }
        this.runPrintTask(
            buildTask(selectedOrders),
            buildSuccessMessage(selectedOrders.length),
        );
    }

    private requestInvoicePrinting(
        orders: UnifiedOrder[],
        saveLastPrint = true,
    ): Observable<unknown> {
        // The render server keeps pages in request order, and the shipping
        // labels are necessarily grouped by platform (one render per
        // template); iterate the same grouping here so the invoice and label
        // stacks pair up sheet by sheet. The incoming orders are already
        // sorted by store/creation, which groupBy preserves within each
        // platform.
        const orderedOrders = _flatten(_values(_groupBy(orders, 'platform')));
        // Orders diverted to the plain path because their issuer's tax
        // invoices are made externally (FlowAccount). Filled during the
        // partition, read at print time for the reminder popup.
        const externalKeys = new Set<string>();
        // Look up the customer's own billing request first, then the recognized
        // receipt/customer details; when either is present the invoice is
        // issued from it instead of the platform's data.
        return forkJoin([
            this.fetchBillingRequests(orderedOrders),
            this.fetchReceiptOrders(orderedOrders),
        ]).pipe(
            switchMap(
                ([billingByOrderId, receiptByKey]: [
                    Map<string, InvoiceRequest[]>,
                    Map<string, ReceiptOrder>,
                ]) => {
                    // One pick per order, shared by the partition predicate,
                    // resolve() and the issue requests — so the eligibility
                    // decision and the printed override cannot disagree.
                    const pickedByKey = new Map<
                        string,
                        InvoiceRequest | undefined
                    >(
                        _map(
                            orderedOrders,
                            (
                                order: UnifiedOrder,
                            ): [string, InvoiceRequest | undefined] => [
                                this.receiptKey(order),
                                this.pickBillingRequest(
                                    billingByOrderId.get(order.orderId),
                                    order,
                                ),
                            ],
                        ),
                    );
                    // W14: an order gets the FULL tax-invoice document set
                    // (one sheet: delivery note / tax invoice / receipt) only
                    // when BOTH hold — a registered issuer exists for it (the
                    // store's owner when VAT registered at the order date, or
                    // the store's requestedTaxInvoiceEntityId when the
                    // customer explicitly asked for a tax invoice) AND the
                    // buyer's details are confirmed (a billing request or a
                    // Tier-1 recognizer receipt). Everything else keeps the
                    // plain invoice sheet: the console never issues
                    // ใบกำกับภาษีอย่างย่อ (decided 2026-09-02), so an order
                    // with no confirmed buyer is not a tax invoice, whoever
                    // its seller is.
                    //
                    // Exception (2026-08-13): an issuer whose
                    // taxInvoiceExternalPlatforms lists the order's platform
                    // (the company's Lazada sales — FlowAccount) gets NO
                    // console tax invoice. Those orders take the plain path,
                    // remembered in externalKeys so the print step can pop
                    // the FlowAccount reminder for the ones carrying
                    // confirmed full-invoice details.
                    const [taxOrders, plainOrders] = _partition(
                        orderedOrders,
                        (order: UnifiedOrder) => {
                            const key = this.receiptKey(order);
                            const billingRequest = pickedByKey.get(key);
                            const issuer = this.taxInvoiceIssuerFor(
                                order,
                                CombinedOrdersComponent.isTaxInvoiceRequested(
                                    billingRequest,
                                ),
                            );
                            if (issuer === null) {
                                return false;
                            }
                            if (
                                CombinedOrdersComponent.issuesExternally(
                                    issuer,
                                    order.platform,
                                )
                            ) {
                                externalKeys.add(key);
                                return false;
                            }
                            return this.hasConfirmedBuyer(
                                billingRequest,
                                receiptByKey.get(key),
                            );
                        },
                    );
                    const resolve = (
                        order: UnifiedOrder,
                    ): Observable<ResolvedInvoiceOrder | null> => {
                        const billingRequest = pickedByKey.get(
                            this.receiptKey(order),
                        );
                        const receiptOrder = receiptByKey.get(
                            this.receiptKey(order),
                        );
                        return this.buildInvoiceParam(order).pipe(
                            map(
                                (
                                    param: InvoiceParameter,
                                ): ResolvedInvoiceOrder => ({
                                    order,
                                    billingRequest,
                                    receiptOrder,
                                    param: billingRequest
                                        ? this.applyBillingRequest(
                                              param,
                                              billingRequest,
                                              order,
                                          )
                                        : this.applyReceiptInfo(
                                              param,
                                              order,
                                              receiptOrder,
                                          ),
                                }),
                            ),
                            catchError((error) => {
                                this.notifyError(
                                    error,
                                    `${order.storeTitle} ${order.orderId}`,
                                );
                                return of(null);
                            }),
                        );
                    };
                    // forkJoin([]) completes without emitting — guard both.
                    const resolveAll = (
                        list: UnifiedOrder[],
                    ): Observable<(ResolvedInvoiceOrder | null)[]> =>
                        _isEmpty(list) ? of([]) : forkJoin(_map(list, resolve));
                    return forkJoin([
                        resolveAll(plainOrders),
                        resolveAll(taxOrders),
                    ]);
                },
            ),
            switchMap(
                ([plainResolved, taxResolved]: [
                    (ResolvedInvoiceOrder | null)[],
                    (ResolvedInvoiceOrder | null)[],
                ]) => {
                    const plainTargets = _filter(
                        plainResolved,
                        (r) => !!r,
                    ) as ResolvedInvoiceOrder[];
                    const taxReady = _filter(
                        taxResolved,
                        (r) => !!r,
                    ) as ResolvedInvoiceOrder[];
                    // The partition admitted these on a confirmed buyer, so
                    // the null branch is unreachable; it is a filter rather
                    // than an assertion so a future partition edit degrades
                    // to "no tax invoice", never to a document with an empty
                    // buyer block.
                    const issueRequests: TaxInvoiceIssueRequest[] = [];
                    _each(taxReady, (resolved) => {
                        const buyer = this.taxInvoiceBuyerFor(resolved);
                        if (buyer) {
                            issueRequests.push({
                                order: resolved.order,
                                invParam: resolved.param,
                                buyer,
                                hasTaxInvoiceRequest:
                                    CombinedOrdersComponent.isTaxInvoiceRequested(
                                        resolved.billingRequest,
                                    ),
                            });
                        }
                    });
                    const issue$ = _isEmpty(issueRequests)
                        ? of({
                              targets: [],
                              skipped: [],
                              warnings: [],
                          } as TaxInvoiceIssueResult)
                        : this.taxInvoiceIssueService.ensureTaxInvoices(
                              issueRequests,
                          );
                    return issue$.pipe(
                        switchMap((issue: TaxInvoiceIssueResult) => {
                            this.notifyTaxInvoiceIssue(issue);
                            if (_isEmpty(plainTargets) && _isEmpty(taxReady)) {
                                return throwError(
                                    new Error('No invoices could be generated'),
                                );
                            }
                            // Every resolved order gets its plain receipt
                            // sheet in ONE tab, in orderedOrders sequence —
                            // the same sequence the shipping labels print
                            // in — so the two stacks pair up at the pack
                            // bench. The tax invoices open in a SECOND tab
                            // at the template's own full-page size: those
                            // are printed one per sheet, not two-up like
                            // the receipts, so the stack must not be
                            // interleaved or scaled. No tab when nothing
                            // was issued or reprinted — a blank page is
                            // just noise at the bench.
                            const plainParamByKey = new Map(
                                _map(
                                    [...plainTargets, ...taxReady],
                                    (resolved): [string, InvoiceParameter] => [
                                        this.receiptKey(resolved.order),
                                        resolved.param,
                                    ],
                                ),
                            );
                            const receiptParams: InvoiceParameter[] = [];
                            _each(orderedOrders, (order: UnifiedOrder) => {
                                const param = plainParamByKey.get(
                                    this.receiptKey(order),
                                );
                                if (param) {
                                    receiptParams.push(param);
                                }
                            });
                            const prints: Observable<unknown>[] = [
                                this.printSheetService.printInvoice(
                                    receiptParams,
                                    'Invoices',
                                ),
                            ];
                            if (!_isEmpty(issue.targets)) {
                                prints.push(
                                    this.printSheetService.printTaxInvoice(
                                        _map(issue.targets, 'page'),
                                        'Tax Invoices',
                                    ),
                                );
                            }
                            prints.push(
                                saveLastPrint
                                    ? this.saveLastPrints(orders)
                                    : of([]),
                            );
                            // A billing request is consumed by a plain print
                            // or by the full tax invoice issued from it.
                            const issuedKeys = new Set(
                                _map(issue.targets, (target) =>
                                    this.receiptKey(target.order),
                                ),
                            );
                            const billingTargets: InvoicePrintTarget[] = [
                                ...plainTargets,
                                ..._filter(
                                    taxReady,
                                    (resolved) =>
                                        !!resolved.billingRequest &&
                                        issuedKeys.has(
                                            this.receiptKey(resolved.order),
                                        ),
                                ),
                            ];
                            // Orders whose issuer works in FlowAccount AND
                            // whose full-invoice buyer details are confirmed:
                            // these are the ones someone must issue there.
                            const flowAccountTargets = _filter(
                                plainTargets,
                                (resolved) =>
                                    externalKeys.has(
                                        this.receiptKey(resolved.order),
                                    ) &&
                                    this.taxInvoiceBuyerFor(resolved) !== null,
                            );
                            return forkJoin(prints).pipe(
                                // The parcel documents are out; archiving the
                                // copy of record and the status transitions
                                // run inside the same task so failures are
                                // named while the spinner still shows.
                                switchMap(() => {
                                    this.notifyFlowAccountIssuance(
                                        flowAccountTargets,
                                    );
                                    return this.archiveIssuedTaxInvoices(
                                        issue.targets,
                                    );
                                }),
                                switchMap(() =>
                                    this.markBillingRequestsIssued(
                                        billingTargets,
                                    ),
                                ),
                            );
                        }),
                    );
                },
            ),
        );
    }

    // W14 eligibility: the tax-invoice issuer resolved AT the order's
    // creation date (the tax point) — the store's owner when it is VAT
    // registered at that date, else the store's requested-tax-invoice
    // issuer when the customer explicitly asked for one. Errors mean "no
    // issuer", never a crash — the order falls to the plain path.
    private taxInvoiceIssuerFor(
        order: UnifiedOrder,
        hasTaxInvoiceRequest: boolean,
    ): EntityId | null {
        if (!order.createDate) {
            return null;
        }
        try {
            const store = storeById(order.storeId as StoreId);
            const dateIso = moment(order.createDate).format('YYYY-MM-DD');
            return taxInvoiceEntityIdFor(store, dateIso, hasTaxInvoiceRequest);
        } catch {
            return null;
        }
    }

    // An issuer whose tax invoices for THIS platform live in an external
    // system (FlowAccount handles the company's Lazada sales) gets no
    // console-generated document — those orders take the plain path. Other
    // platforms keep the console tax flow.
    private static issuesExternally(
        entityId: EntityId,
        platform: string,
    ): boolean {
        const entity = entityById(entityId);
        return (
            entity.vatRegistered === true &&
            (entity.taxInvoiceExternalPlatforms as readonly string[])?.includes(
                platform,
            ) === true
        );
    }

    // A tax invoice needs confirmed buyer details — the customer's own
    // billing request, or a Tier-1 confirmed recognizer receipt. Without
    // either there is no tax invoice at all (the console never issues
    // ใบกำกับภาษีอย่างย่อ); the partition uses this same predicate so the
    // routing decision and the document cannot disagree.
    private hasConfirmedBuyer(
        billingRequest: InvoiceRequest | undefined,
        receiptOrder: ReceiptOrder | undefined,
    ): boolean {
        return !!billingRequest || this.isTier1Available(receiptOrder);
    }

    // The buyer block for the FULL tax invoice: both sources set text
    // name/address/taxId on the param. null when neither is present.
    private taxInvoiceBuyerFor(
        resolved: ResolvedInvoiceOrder,
    ): TaxInvoiceBuyer | null {
        if (
            !this.hasConfirmedBuyer(
                resolved.billingRequest,
                resolved.receiptOrder,
            )
        ) {
            return null;
        }
        const customer = resolved.param.customerInfo;
        return {
            name: _trim(_toString(customer.name)),
            address: _trim(_toString(customer.address ?? '')),
            taxId: _trim(_toString(customer.taxId ?? '')),
            branch: _trim(_toString(customer.branch ?? '')),
        };
    }

    // The console prints these orders' plain invoices, but their FULL tax
    // invoices must be issued in FlowAccount — a popup, not a toast, because
    // a missed full-invoice request carries a customer promise. Fired after
    // the prints are out so it never blocks the documents.
    private notifyFlowAccountIssuance(targets: ResolvedInvoiceOrder[]): void {
        if (_isEmpty(targets)) {
            return;
        }
        const lines = _map(
            targets,
            (resolved) =>
                `${_escape(resolved.order.orderId)} — ${_escape(
                    _toString(resolved.param.customerInfo.name),
                )}`,
        );
        dxAlert(
            `<div style="text-align: left; line-height: 1.6;">` +
                `ลูกค้าขอใบกำกับภาษีเต็มรูป — ออกใบกำกับภาษีใน FlowAccount ` +
                `สำหรับคำสั่งซื้อต่อไปนี้:<br><br>${lines.join('<br>')}</div>`,
            'ออกใบกำกับภาษีใน FlowAccount',
        );
    }

    // Skips are counted and named — never silent (spec W6 acceptance shape);
    // W13 warnings surface once per run.
    private notifyTaxInvoiceIssue(issue: TaxInvoiceIssueResult): void {
        if (!_isEmpty(issue.skipped)) {
            const listed = _take(
                _map(
                    issue.skipped,
                    (skip) => `${skip.order.orderId}: ${skip.reason}`,
                ),
                CombinedOrdersComponent.MAX_ORDERS_LISTED_IN_MESSAGE,
            ).join(' / ');
            this.notifyError(
                new Error(listed),
                `${issue.skipped.length} tax invoice(s) skipped`,
            );
        }
        if (!_isEmpty(issue.warnings)) {
            this.notificationService.pushMessage({
                message: _take(
                    issue.warnings,
                    CombinedOrdersComponent.MAX_ORDERS_LISTED_IN_MESSAGE,
                ).join(' / '),
                messageType: MessageType.warning,
            });
        }
    }

    // The copy of record (user decision): the parcel carries the ต้นฉบับ
    // only; every invoice generated this run is rendered server-side and
    // archived as PDF in the entity's Drive folder, with the shared link
    // written back onto the row. Rows with no fileSharedLink are visible on
    // the report page, which is the completeness check.
    private archiveIssuedTaxInvoices(
        targets: TaxInvoiceIssueTarget[],
    ): Observable<unknown> {
        const generated = _filter(targets, 'generated');
        if (_isEmpty(generated)) {
            return of(null);
        }
        const failures: string[] = [];
        return from(generated).pipe(
            mergeMap(
                (target: TaxInvoiceIssueTarget) =>
                    this.renderPersistService
                        .renderAndGetSharedUrl<TaxInvoicePrintSheetParameter>({
                            renderRequest: {
                                template: {
                                    name: environment.taxInvoiceReportTemplateName,
                                },
                                data: { pages: [target.page] },
                            },
                            reportStoreRequest: {
                                fileName: `${target.invNum}.pdf`,
                                parents: [
                                    vatRegisteredEntityById(target.entityId)
                                        .invoiceDriveFolderId,
                                ],
                            },
                        })
                        .pipe(
                            switchMap((response: any) =>
                                this.outputTaxService.saveTaxInvoiceSharedUrl(
                                    target.invNum,
                                    response.webViewLink,
                                ),
                            ),
                            catchError((error) => {
                                console.error(error);
                                failures.push(target.invNum);
                                return of(null);
                            }),
                        ),
                CombinedOrdersComponent.ARCHIVE_CONCURRENCY,
            ),
            toArray(),
            map(() => {
                if (!_isEmpty(failures)) {
                    this.notifyError(
                        new Error(failures.join(', ')),
                        `Archive failed for ${failures.length} tax invoice(s) — retry via the report page`,
                    );
                }
                return null;
            }),
        );
    }

    // Looks up the customer's own billing requests (billing-request-collector)
    // for the given orders and returns the usable candidates keyed by order
    // number. Lookup is by order number alone — storeId is free-form in the
    // collector, so scoping to the console's storeId could miss records
    // written under another spelling. Candidates are narrowed to a single
    // record per order later by pickBillingRequest, which is what applies the
    // order's platform. Orders go out as comma-separated orderSn batches so a
    // full Ready to Ship tab is one or two calls rather than one per order —
    // the burst that used to trip the Lambda's concurrency throttle.
    private fetchBillingRequests(
        orders: UnifiedOrder[],
    ): Observable<Map<string, InvoiceRequest[]>> {
        const uniqueOrders = _uniqBy(
            _filter(orders, (order: UnifiedOrder) => !!order.orderId),
            'orderId',
        );
        if (_isEmpty(uniqueOrders)) {
            return of(new Map<string, InvoiceRequest[]>());
        }
        const failures: { orderId: string; error: unknown }[] = [];
        const batches = _chunk(
            uniqueOrders,
            CombinedOrdersComponent.BILLING_LOOKUP_BATCH_SIZE,
        );
        const lookups = _map(batches, (batch: UnifiedOrder[]) =>
            // defer so a retry re-issues the requests: listAll starts its
            // page loop when invoked, not when subscribed, so retrying the
            // bare observable would just replay the settled failure.
            defer(() =>
                this.invoiceRequestService.listAll({
                    orderSn: _map(batch, 'orderId').join(','),
                    status: 'complete,issued',
                }),
            ).pipe(
                retryWhen((errors: Observable<unknown>) =>
                    errors.pipe(
                        mergeMap((error: unknown, attempt: number) =>
                            attempt <
                                CombinedOrdersComponent.BILLING_LOOKUP_RETRIES &&
                            CombinedOrdersComponent.isThrottleError(error)
                                ? timer(1000 * (attempt + 1))
                                : throwError(error),
                        ),
                    ),
                ),
                // Keep one failed batch from collapsing the whole load.
                catchError((error) => {
                    _each(batch, (order: UnifiedOrder) =>
                        failures.push({ orderId: order.orderId, error }),
                    );
                    return of([] as InvoiceRequest[]);
                }),
            ),
        );
        return forkJoin(lookups).pipe(
            map((batchResults: InvoiceRequest[][]) => {
                if (!_isEmpty(failures)) {
                    this.notifyBillingLookupFailures(failures);
                }
                const usable = _filter(
                    _flatten(batchResults),
                    (item: InvoiceRequest) =>
                        CombinedOrdersComponent.isUsableBillingRequest(item),
                );
                // A batch response carries the stored orderSn (trimmed,
                // upper-cased) in no particular order, and orders without a
                // record are simply absent — so match by value, never by
                // position, and treat absence as "no billing request yet".
                const itemsByOrderSn = _groupBy(usable, 'orderSn');
                const lookup = new Map<string, InvoiceRequest[]>();
                _each(uniqueOrders, (order: UnifiedOrder) => {
                    const items =
                        itemsByOrderSn[_trim(order.orderId).toUpperCase()];
                    if (!_isEmpty(items)) {
                        lookup.set(order.orderId, items);
                    }
                });
                return lookup;
            }),
        );
    }

    // 429 is the gateway's rate limit, 503 the Lambda's concurrency limit;
    // 0 is the browser reporting a failed request outright, which is how a
    // throttled CORS preflight surfaces.
    private static isThrottleError(error: unknown): boolean {
        const status = _get(error, 'status');
        return status === 429 || status === 503 || status === 0;
    }

    // A lookup failure doesn't block the print — the invoice falls back to the
    // receipt/platform data — but that invoice may then carry the wrong
    // customer, so name the orders it happened to rather than raising one
    // anonymous error beside a green success toast. Always fires before the
    // render, so the wording holds for the indicator pass too.
    private notifyBillingLookupFailures(
        failures: { orderId: string; error: unknown }[],
    ): void {
        const orderIds = _map(failures, 'orderId');
        const listed = _take(
            orderIds,
            CombinedOrdersComponent.MAX_ORDERS_LISTED_IN_MESSAGE,
        ).join(', ');
        const remaining =
            orderIds.length -
            CombinedOrdersComponent.MAX_ORDERS_LISTED_IN_MESSAGE;
        this.notifyError(
            failures[0].error,
            `Billing request lookup failed for ${orderIds.length} order(s) — ` +
                `they will print from platform data: ${listed}` +
                (remaining > 0 ? ` (and ${remaining} more)` : ''),
        );
    }

    // The API filters by status, but an erased record keeps its status while
    // its PII is nulled. Requiring the name and address outright means the
    // override is all-or-nothing: a partial record is never allowed to mix the
    // customer's identity with the platform's.
    private static isUsableBillingRequest(item: InvoiceRequest): boolean {
        if (
            item.erased ||
            (item.status !== 'complete' && item.status !== 'issued')
        ) {
            return false;
        }
        const invoice = item.invoice || {};
        return (
            !_isEmpty(_trim(_toString(invoice.legalName))) &&
            !_isEmpty(
                PrintSheetUtils.constructThaiFullAddress(invoice.address || {}),
            )
        );
    }

    // Of the candidates for an order number, the record's platform must not
    // contradict the order's: a marketplace record only matches its own
    // marketplace, while 'other'/'web' records (manual entry and the public
    // form) carry no marketplace and match any order. Without this, two orders
    // that happen to share an order number on different marketplaces would both
    // bind the same record. Ties break on the most recently updated.
    private pickBillingRequest(
        items: InvoiceRequest[] | undefined,
        order: UnifiedOrder,
    ): InvoiceRequest | undefined {
        const platform = COLLECTOR_PLATFORMS[order.platform];
        const matching = _filter(
            items,
            (item: InvoiceRequest) =>
                item.platform === platform ||
                _includes(PLATFORM_AGNOSTIC, item.platform),
        );
        return _orderBy(
            matching,
            [
                (item: InvoiceRequest) => (item.platform === platform ? 0 : 1),
                (item: InvoiceRequest) => item.audit?.updatedAt || '',
            ],
            ['asc', 'desc'],
        )[0];
    }

    // Same predicate the red grid indicator keys off (billingReceiptDetail):
    // only an explicit customer request FOR A TAX INVOICE triggers the
    // requested-issuer override — 'receipt-only' requests do not.
    private static isTaxInvoiceRequested(request?: InvoiceRequest): boolean {
        return request?.requestType === 'tax-invoice';
    }

    // The billing request's customer block in the recognizer's shape, so the
    // invoice override and the receipt tooltip both reuse the receipt helpers
    // and cannot drift apart.
    private toReceiptInfo(request: InvoiceRequest): ReceiptInfo {
        const invoice = request.invoice || {};
        return {
            name: _trim(_toString(invoice.legalName)),
            taxId: invoice.taxId || undefined,
            branchNo: CombinedOrdersComponent.toBranchNo(invoice.branchCode),
            address: PrintSheetUtils.constructThaiFullAddress(
                invoice.address || {},
            ),
        };
    }

    // The collector stores the branch as a 5-digit code ("00000" = head
    // office). Anything else is free-form data that isn't a branch number —
    // note that a bare Number() would turn ' ' into 0, i.e. head office.
    private static toBranchNo(
        branchCode: string | null | undefined,
    ): number | undefined {
        const code = _trim(_toString(branchCode));
        return /^[0-9]+$/.test(code) ? Number(code) : undefined;
    }

    // Issues the invoice from the customer's own billing request: their legal
    // name, tax id, branch and address replace whatever the platform or the
    // recognizer supplied.
    private applyBillingRequest(
        param: InvoiceParameter,
        request: InvoiceRequest,
        order: UnifiedOrder,
    ): InvoiceParameter {
        const info = this.toReceiptInfo(request);
        return {
            ...param,
            sellerInfo: this.sellerInfoFor(param, info.taxId, order),
            customerInfo: {
                ...param.customerInfo,
                // Shopee renders the recipient name and address as images;
                // both are cleared so neither can paint over the customer's own
                // details. isUsableBillingRequest guarantees both texts exist.
                name: _toString(info.name),
                name_img_src: undefined,
                address: info.address,
                address_img_src: undefined,
                taxId: info.taxId,
                branch: this.formatReceiptBranch(info.branchNo),
                // The customer asked for the document without a date.
                ...(request.specialRequest?.billWithNoDate ? { date: '' } : {}),
            },
        };
    }

    // Grid cell value for the read-only Entity column: the taxpayer whose
    // sale each order is, derived from the store (CONSOLE-BUILD-SPEC W1).
    // The column exists so the store→entity mapping can be eyeballed
    // against real orders before documents depend on it. Arrow property
    // because DevExtreme calls calculateCellValue unbound.
    entityIdFor = (order: UnifiedOrder): string => {
        try {
            return storeById(order.storeId as StoreId).entityId;
        } catch {
            // A storeId outside the config would already be a bug elsewhere;
            // the grid shows the gap instead of throwing mid-render.
            return '';
        }
    };

    // A customer block carrying a tax ID makes the document a tax receipt,
    // which must also carry the seller's tax registration — the taxpayer
    // whose sale this order IS, from sellerFor (W1), never a per-store
    // literal.
    private sellerInfoFor(
        param: InvoiceParameter,
        customerTaxId: string | undefined,
        order: UnifiedOrder,
    ): InvoiceParameter['sellerInfo'] {
        if (!customerTaxId) {
            return param.sellerInfo;
        }
        const seller = sellerFor({ storeId: order.storeId as StoreId });
        return {
            ...param.sellerInfo,
            taxId: seller.taxId,
            branch: seller.branch,
        };
    }

    // complete -> issued for the records this print was issued from. Records
    // already `issued` are left alone, which keeps reprints harmless. Never
    // fails the print task — the PDF is already open by the time this runs,
    // and runPrintTask keeps the grid spinner up until this settles, so the
    // transitions run a few at a time rather than one after another.
    private markBillingRequestsIssued(
        targets: InvoicePrintTarget[],
    ): Observable<unknown> {
        // Deduplicated by id: two selected rows sharing an order number would
        // otherwise transition the same record twice, and the second call would
        // fail on a stale If-Match version.
        const requests = _uniqBy(
            _filter(
                _map(targets, 'billingRequest'),
                (request?: InvoiceRequest) => request?.status === 'complete',
            ) as InvoiceRequest[],
            'id',
        );
        if (_isEmpty(requests)) {
            return of(null);
        }
        const failures: unknown[] = [];
        return from(requests).pipe(
            // Each record carries its own id and version, so these are
            // independent — bounded only to avoid hammering the API.
            mergeMap(
                (request: InvoiceRequest) =>
                    this.invoiceRequestService
                        .markIssued(request.id, request.version)
                        .pipe(
                            catchError((error) => {
                                failures.push(error);
                                return of(null);
                            }),
                        ),
                CombinedOrdersComponent.MARK_ISSUED_CONCURRENCY,
            ),
            toArray(),
            map(() => {
                const issued = requests.length - failures.length;
                if (issued > 0) {
                    this.notifySuccess(
                        `Marked ${issued} billing request(s) issued`,
                    );
                }
                if (!_isEmpty(failures)) {
                    this.notifyError(failures[0], 'Mark issued');
                }
                return null;
            }),
        );
    }

    // Fetches the receipt_orders documents for the given orders in one query
    // and returns them keyed by platform:orderId. Unfiltered so both confirmed
    // tax receipts and recognized-only documents are available.
    private fetchReceiptOrders(
        orders: UnifiedOrder[],
    ): Observable<Map<string, ReceiptOrder>> {
        if (_isEmpty(orders)) {
            return of(new Map());
        }
        const orderConditions = _map(orders, (order: UnifiedOrder) => ({
            platform: order.platform,
            orderId: order.orderId,
        }));
        return this.unifiedQueryV2Service
            .query<ReceiptOrder>({
                db: 'receipt_info_recognizer',
                col: 'receipt_orders',
                query: {
                    $or: orderConditions, // eslint-disable-line @typescript-eslint/naming-convention
                },
            })
            .pipe(
                map((receiptOrders: ReceiptOrder[]) => {
                    const lookup = new Map<string, ReceiptOrder>();
                    _each(receiptOrders, (receiptOrder: ReceiptOrder) => {
                        lookup.set(this.receiptKey(receiptOrder), receiptOrder);
                    });
                    return lookup;
                }),
                catchError((error) => {
                    // Don't block printing on a receipt-lookup failure; fall
                    // back to the platform data and let the user know.
                    this.notifyError(error, 'Receipt lookup');
                    return of(new Map<string, ReceiptOrder>());
                }),
            );
    }

    private receiptKey(item: { platform: string; orderId: string }): string {
        return `${item.platform}:${item.orderId}`;
    }

    // On the pre-shipment tabs, flag each order's receipt state so the grid can
    // show whether the customer's own billing request or a confirmed tax
    // receipt (Tier 1, any platform) exists. The missing-receipt warning is
    // raised only on Ready to Ship — see below.
    private attachReceiptIndicators(
        orders: UnifiedOrder[],
        orderStatus: UnifiedOrderStatus,
        // The background poll only re-checks orders that already had no billing
        // request, so it skips the collector rather than issuing a call per
        // order every few seconds: a late request still surfaces through the
        // recognizer, which ingests them as receiptInfo.source
        // 'invoice_request'.
        includeBillingRequests = true,
    ): Observable<UnifiedOrder[]> {
        const isPreShipment =
            orderStatus === UnifiedOrderStatus.ToShip ||
            orderStatus === UnifiedOrderStatus.ReadyToShip;
        if (!isPreShipment || _isEmpty(orders)) {
            return of(orders);
        }
        return forkJoin([
            includeBillingRequests
                ? this.fetchBillingRequests(orders)
                : of(new Map<string, InvoiceRequest[]>()),
            this.fetchReceiptOrders(orders),
        ]).pipe(
            map(
                ([billingByOrderId, receiptByKey]: [
                    Map<string, InvoiceRequest[]>,
                    Map<string, ReceiptOrder>,
                ]) => {
                    _each(orders, (order: UnifiedOrder) => {
                        const receiptOrder = receiptByKey.get(
                            this.receiptKey(order),
                        );
                        const billingRequest = this.pickBillingRequest(
                            billingByOrderId.get(order.orderId),
                            order,
                        );
                        if (billingRequest) {
                            this.billingRequestsByOrderKey.set(
                                order.key,
                                billingRequest,
                            );
                            order.receiptAvailable = true;
                            order.receiptDetail =
                                this.billingReceiptDetail(billingRequest);
                        } else {
                            order.receiptAvailable =
                                this.isTier1Available(receiptOrder);
                            order.receiptDetail = order.receiptAvailable
                                ? this.recognizedReceiptDetail(
                                      receiptOrder?.receiptInfo,
                                  )
                                : undefined;
                        }
                        // Only warn once the order is about to be printed: on
                        // To Ship the receipt info may still be on its way, so
                        // "the invoice will use the label image" would be
                        // premature there.
                        order.missingReceiptData =
                            orderStatus === UnifiedOrderStatus.ReadyToShip &&
                            order.platform === 'shopee' &&
                            !order.receiptAvailable &&
                            !this.isShopeeTier2Available(receiptOrder);
                    });
                    return orders;
                },
            ),
        );
    }

    private isTier1Available(receiptOrder: ReceiptOrder | undefined): boolean {
        return !!(receiptOrder?.receiptAvailable && receiptOrder?.receiptInfo);
    }

    private isShopeeTier2Available(
        receiptOrder: ReceiptOrder | undefined,
    ): boolean {
        return !!(receiptOrder?.customerName && receiptOrder?.customerAddress);
    }

    // Builds the multi-line tax-receipt detail shown in the receipt tooltip
    // and popup: name, tax id, branch, and address.
    // How much the details can be trusted depends on where they came from: the
    // customer's own submission is authoritative, a company inferred from the
    // shipping recipient much less so. Label them rather than leaving the
    // operator to guess.
    private static readonly RECOGNIZER_SOURCE_LABELS: Record<string, string> = {
        invoice_request: "buyer's request on the platform",
        order_remark: 'order remark',
        recipient_match: 'recipient name/address',
    };

    private static readonly BILLING_SOURCE_LABELS: Record<string, string> = {
        chatbot: 'chatbot',
        web: 'web form',
        manual: 'entered here',
    };

    // A billing request is always the customer's own submission, whichever
    // channel carried it — including one typed in here from what they said.
    private billingReceiptDetail(request: InvoiceRequest): ReceiptDetail {
        return this.buildReceiptDetail(
            this.toReceiptInfo(request),
            CombinedOrdersComponent.BILLING_SOURCE_LABELS[request.source] ||
                request.source,
            'stated',
            CombinedOrdersComponent.isTaxInvoiceRequested(request),
        );
    }

    // The recognizer either read the buyer's own request off the platform or
    // worked the customer out from the order; only the first is stated.
    private recognizedReceiptDetail(
        receiptInfo: ReceiptInfo | undefined,
    ): ReceiptDetail {
        const source = _toString(receiptInfo?.source);
        return this.buildReceiptDetail(
            receiptInfo,
            CombinedOrdersComponent.RECOGNIZER_SOURCE_LABELS[source] ||
                source ||
                'recognized from the order',
            source === 'invoice_request' ? 'stated' : 'inferred',
        );
    }

    private buildReceiptDetail(
        receiptInfo: ReceiptInfo | undefined,
        source: string,
        confidence: ReceiptDetail['confidence'],
        taxInvoiceRequested = false,
    ): ReceiptDetail {
        return {
            name: _toString(receiptInfo?.name),
            taxId: _toString(receiptInfo?.taxId),
            branch: this.formatReceiptBranch(receiptInfo?.branchNo) || '',
            address: _toString(receiptInfo?.address),
            source,
            taxInvoiceRequested,
            confidence,
        };
    }

    // Flattened for the popup's Copy button.
    private receiptDetailToText(detail: ReceiptDetail): string {
        return _filter([
            detail.name,
            detail.taxId ? `Tax ID: ${detail.taxId}` : '',
            detail.branch,
            detail.address,
            `Source: ${detail.source}`,
        ]).join('\n');
    }

    // Resolves the invoice's customer block from the most authoritative source
    // below the customer's own billing request (which applyBillingRequest
    // handles, and which outranks all of these):
    //   1. a confirmed tax receipt (receiptInfo) — any platform;
    //   2. the recognized customerName/customerAddress — Shopee only;
    //   3. otherwise the platform data already on `param`.
    // Shopee renders the recipient name/address as images, so those are cleared
    // whenever recognized text replaces them.
    private applyReceiptInfo(
        param: InvoiceParameter,
        order: UnifiedOrder,
        receiptOrder: ReceiptOrder | undefined,
    ): InvoiceParameter {
        if (!receiptOrder) {
            return param;
        }
        // Tier 1: confirmed tax receipt.
        if (receiptOrder.receiptAvailable && receiptOrder.receiptInfo) {
            const receiptInfo = receiptOrder.receiptInfo;
            const taxId = receiptInfo.taxId;
            return {
                ...param,
                sellerInfo: this.sellerInfoFor(param, taxId, order),
                customerInfo: {
                    ...param.customerInfo,
                    name: receiptInfo.name || param.customerInfo.name,
                    name_img_src: undefined,
                    address: receiptInfo.address || param.customerInfo.address,
                    address_img_src: undefined,
                    taxId,
                    branch: this.formatReceiptBranch(receiptInfo.branchNo),
                },
            };
        }
        // Tier 2 (Shopee only): recognized recipient name/address as text.
        if (
            order.platform === 'shopee' &&
            receiptOrder.customerName &&
            receiptOrder.customerAddress
        ) {
            return {
                ...param,
                customerInfo: {
                    ...param.customerInfo,
                    name: receiptOrder.customerName,
                    name_img_src: undefined,
                    address: receiptOrder.customerAddress,
                    address_img_src: undefined,
                },
            };
        }
        // Tier 3: platform data already on `param`.
        return param;
    }

    // Renders the branch as it should appear on the invoice: head office for
    // branch 0, otherwise the zero-padded branch number (e.g. 1 -> 00001).
    private formatReceiptBranch(
        branchNo: number | undefined,
    ): string | undefined {
        if (_isNil(branchNo)) {
            return undefined;
        }
        return branchNo === 0
            ? 'สำนักงานใหญ่'
            : `สาขาที่ ${_padStart(_toString(branchNo), 5, '0')}`;
    }

    // Collapses runs of whitespace (spaces, tabs, newlines) so the remark
    // prints on a single line in the invoice.
    private toInvoiceRemark(remarks: string): string {
        return _trim((remarks || '').replace(/\s+/g, ' '));
    }

    private buildInvoiceParam(
        order: UnifiedOrder,
    ): Observable<InvoiceParameter> {
        switch (order.platform) {
            case 'lazada':
                return of(this.buildLazadaInvoiceParam(order));
            case 'shopee':
                return this.buildShopeeInvoiceParam(order);
            case 'tiktok':
                return of(this.buildTiktokInvoiceParam(order));
        }
    }

    // Formats a date as a Thai Buddhist-era string (Thai month names + BE
    // year) for the printed documents, e.g. 16 มิ.ย. 2569.
    private toBuddhistDate(
        value: moment.Moment | Date | null | undefined,
    ): string {
        const parsed = moment.isMoment(value)
            ? value
            : value
              ? moment(value)
              : null;
        if (!parsed || !parsed.isValid()) {
            return '';
        }
        return formatBuddhistEra(parsed, environment.printDateFormat);
    }

    private buildLazadaInvoiceParam(order: UnifiedOrder): InvoiceParameter {
        const raw = order.raw;
        const originalTotalItemPrice = this.utilService.localStringToNumber(
            raw.price,
        );
        const totalDiscount = this.utilService.localStringToNumber(raw.voucher);
        const shippingFee = this.utilService.localStringToNumber(
            raw.shipping_fee,
        );
        const netItemPrice =
            originalTotalItemPrice + shippingFee - totalDiscount;
        const print = this.getPrintInfo(order);
        return {
            sellerInfo: {
                name: print.name,
                website: print.website,
                address: print.address,
            },
            customerInfo: {
                name: this.getLazadaFullName(raw.address_billing),
                address: this.getLazadaFullAddress(raw.address_billing),
                orderNo: order.orderId,
                date: this.toBuddhistDate(
                    this.lazadaFormatDateTimePipe.tryParseToMoment(
                        raw.created_at,
                    ),
                ),
            },
            orderInfo: {
                orderNo: order.orderId,
                matchingNumber: order.matching,
                total: originalTotalItemPrice,
                discount: totalDiscount,
                shippingCost: shippingFee,
                netPrice: netItemPrice,
                remark: this.toInvoiceRemark(order.remarks),
            },
            items: order.items,
        };
    }

    private buildShopeeInvoiceParam(
        order: UnifiedOrder,
    ): Observable<InvoiceParameter> {
        const service = this.getStoreInjector(order).get(ShopeeOpenServiceV2);
        return service.getShippingDocInfo(order.orderId, 10, 7, 8).pipe(
            map((docData: ShopeeShippingDocDataInfo): InvoiceParameter => {
                const raw = order.raw;
                const totalItemPrice = _reduce(
                    raw.item_list,
                    (accumulator: number, item: any) =>
                        accumulator +
                        this.utilService.localStringToNumber(
                            item.model_original_price,
                        ) *
                            this.utilService.localStringToNumber(
                                item.model_quantity_purchased,
                            ),
                    0,
                );
                const shippingFee = raw.actual_shipping_fee_confirmed
                    ? this.utilService.localStringToNumber(
                          raw.actual_shipping_fee,
                      )
                    : this.utilService.localStringToNumber(
                          raw.estimated_shipping_fee,
                      );
                const totalDiscount =
                    totalItemPrice +
                    shippingFee -
                    this.utilService.localStringToNumber(raw.total_amount);
                const print = this.getPrintInfo(order);
                return {
                    sellerInfo: {
                        name: print.name,
                        website: print.website,
                        address: print.address,
                    },
                    customerInfo: {
                        // Shopee's recipient_address.name is masked; rely on
                        // the rendered image and only send the masked text as
                        // a fallback when no image is available.
                        name: docData.recipientNameBase64
                            ? ''
                            : _toString(
                                  _get(raw, 'recipient_address.name') || '',
                              ),
                        name_img_src: docData.recipientNameBase64 ?? undefined,
                        address: docData.recipientFullAddrBase64
                            ? ''
                            : _toString(
                                  _get(raw, 'recipient_address.full_address') ||
                                      '',
                              ),
                        address_img_src:
                            docData.recipientFullAddrBase64 ?? undefined,
                        orderNo: order.orderId,
                        date: this.toBuddhistDate(
                            this.shopeeFormatDateTimePipe.tryParseToMoment(
                                raw.create_time,
                            ),
                        ),
                    },
                    orderInfo: {
                        orderNo: order.orderId,
                        matchingNumber: order.matching,
                        total: totalItemPrice,
                        discount: totalDiscount,
                        shippingCost: shippingFee,
                        netPrice: this.utilService.localStringToNumber(
                            raw.total_amount,
                        ),
                        remark: this.toInvoiceRemark(order.remarks),
                    },
                    items: _map(raw.item_list, (item: any, index: number) => {
                        const quantity = this.utilService.localStringToNumber(
                            item.model_quantity_purchased,
                        );
                        const unitPrice = this.utilService.localStringToNumber(
                            item.model_original_price,
                        );
                        return {
                            itemNo: index + 1,
                            description:
                                PrintSheetUtils.constructItemDescription(
                                    item.item_sku,
                                    item.item_name,
                                    quantity,
                                    item.model_name,
                                    item.model_sku,
                                ),
                            unitPrice,
                            quantity,
                            amount: unitPrice * quantity,
                        };
                    }),
                };
            }),
        );
    }

    private buildTiktokInvoiceParam(order: UnifiedOrder): InvoiceParameter {
        const raw = order.raw;
        // Use the pre-discount amounts: shipping_fee is what the buyer paid
        // after platform subsidies (often 0), while the invoice should show
        // the original fee with the subsidy on the discount line.
        const shippingFee = this.utilService.localStringToNumber(
            _get(raw, 'payment.original_shipping_fee') || 0,
        );
        const items = this.groupUnitItems(
            raw.line_items || [],
            'seller_sku',
            (item: any) => item.product_name,
            (item: any) => item.original_price,
            (item: any) => item.sku_name,
        );
        const totalItemPrice =
            this.utilService.localStringToNumber(
                _get(raw, 'payment.original_total_product_price') || 0,
            ) || _sumBy(items, 'amount');
        const netPrice = order.totalAmount;
        const print = this.getPrintInfo(order);
        return {
            sellerInfo: {
                name: print.name,
                website: print.website,
                address: print.address,
            },
            customerInfo: {
                name: order.recipientName,
                address: this.shortenAsteriskMask(
                    _toString(
                        _get(raw, 'recipient_address.full_address') || '',
                    ),
                ),
                orderNo: order.orderId,
                date: this.toBuddhistDate(order.createDate),
            },
            orderInfo: {
                orderNo: order.orderId,
                matchingNumber: order.matching,
                total: totalItemPrice,
                discount: totalItemPrice + shippingFee - netPrice,
                shippingCost: shippingFee,
                netPrice,
                remark: this.toInvoiceRemark(order.remarks),
            },
            items,
        };
    }

    private requestShippingLabelPrinting(
        orders: UnifiedOrder[],
        saveLastPrint = true,
    ): Observable<unknown> {
        const platformGroups = _groupBy(orders, 'platform');
        const printObservables: Observable<unknown>[] = _map(
            platformGroups,
            (platformOrders: UnifiedOrder[], platform: string) => {
                // Every platform resolves to base64 PDFs so all labels can
                // be merged into a single tab below; TikTok labels are
                // downloaded from TikTok's CDN and stamped with the
                // matching number first.
                let printObservable: Observable<unknown>;
                switch (platform) {
                    case 'lazada':
                        printObservable =
                            this.renderLazadaLabelsPdf(platformOrders);
                        break;
                    case 'shopee':
                        printObservable =
                            this.renderShopeeLabelsPdf(platformOrders);
                        break;
                    case 'tiktok':
                        printObservable =
                            this.renderTiktokLabelsPdf(platformOrders);
                        break;
                    default:
                        printObservable = throwError(
                            new Error(`Unsupported platform: ${platform}`),
                        );
                        break;
                }
                return printObservable.pipe(
                    catchError((error) => {
                        this.notifyError(error, `${platform} shipping labels`);
                        return of(null);
                    }),
                );
            },
        );
        return forkJoin(printObservables).pipe(
            switchMap((results: unknown[]) => {
                if (_every(results, (result) => result === null)) {
                    return throwError(
                        new Error('No shipping labels could be printed'),
                    );
                }
                // Shopee/Lazada yield one base64 PDF each, TikTok an array
                // of them (one per package); failures are null.
                const pdfBase64s = _filter(
                    _flatten(results as any[]),
                    _isString,
                ) as string[];
                const openObservable = _isEmpty(pdfBase64s)
                    ? of(true)
                    : this.printSheetService.openMergedPdfTab(
                          pdfBase64s,
                          'Shipping Labels',
                      );
                return openObservable.pipe(
                    switchMap(() =>
                        saveLastPrint ? this.saveLastPrints(orders) : of([]),
                    ),
                );
            }),
        );
    }

    private renderShopeeLabelsPdf(orders: UnifiedOrder[]): Observable<string> {
        const paramObservables = _map(orders, (order: UnifiedOrder) => {
            const service =
                this.getStoreInjector(order).get(ShopeeOpenServiceV2);
            // The grid fetch usually resolved the tracking number already;
            // refetch it only when it was still unassigned at fetch time.
            const trackingNumberObservable = order.trackingNumber
                ? of(order.trackingNumber)
                : service
                      .getTrackingNumbers([order.orderId])
                      .pipe(
                          map((trackingNumbers: any[]) =>
                              _toString(
                                  _get(trackingNumbers, '[0].trackingNumber') ||
                                      '',
                              ),
                          ),
                      );
            return forkJoin([
                service.getShippingDocInfo(order.orderId, 10, 15),
                trackingNumberObservable,
            ]).pipe(
                map(([docData, trackingNumber]) =>
                    this.buildShopeeLabelParam(order, docData, trackingNumber),
                ),
            );
        });
        return forkJoin(paramObservables).pipe(
            switchMap((labelParams: ShopeeShippingLabelParameter[]) =>
                this.printSheetService.renderShippingLabelBase64(
                    labelParams,
                    environment.shopeeeShippingLabelReportTemplateName,
                    0.335,
                ),
            ),
        );
    }

    private buildShopeeLabelParam(
        order: UnifiedOrder,
        docData: ShopeeShippingDocDataInfo,
        trackingNumber: string,
    ): ShopeeShippingLabelParameter {
        const raw = order.raw;
        const print = this.getPrintInfo(order);
        return {
            matchingNumber: order.matching,
            trackingNumber,
            shippingCarrier: _get(raw, 'shipping_carrier'),
            shippingCarrierLogoUrl: PrintSheetUtils.getShippingCarrierLogo(
                _get(raw, 'shipping_carrier'),
            ),
            sender: {
                name: print.name,
                address: print.address,
            },
            receiver: {
                // recipient_address.name is masked; prefer the rendered image
                // and only send the masked text when no image is available.
                name: docData.recipientNameBase64
                    ? ''
                    : _toString(_get(raw, 'recipient_address.name') || ''),
                name_img_src: docData.recipientNameBase64 ?? undefined,
                address: docData.recipientFullAddrBase64
                    ? ''
                    : _toString(
                          _get(raw, 'recipient_address.full_address') || '',
                      ),
                address_img_src: docData.recipientFullAddrBase64 ?? undefined,
                district: _get(raw, 'recipient_address.city'),
                first_recipient_sort_code: docData.firstRecipientSortCode || '',
            },
            orderInfo: {
                // eslint-disable-next-line id-blacklist
                number: order.orderId,
                creationDate: this.shopeeFormatDateTimePipe.transform(
                    raw.create_time,
                    environment.printDateFormat,
                ),
                shipBy: this.shopeeFormatDateTimePipe.transform(
                    raw.ship_by_date,
                    environment.printDateFormat,
                ),
            },
            codDesc: raw.cod ? 'เก็บเงินปลายทาง' : 'ไม่ต้องเก็บเงิน',
        };
    }

    private renderLazadaLabelsPdf(orders: UnifiedOrder[]): Observable<string> {
        const htmlObservables = _map(orders, (order: UnifiedOrder) => {
            const lexDocService =
                this.getStoreInjector(order).get(LexDocService);
            const orderItemIds: number[] = _map(
                _get(order.raw, 'orderItems') || [],
                'order_item_id',
            );
            return lexDocService.requestShippingLabelHtml(
                orderItemIds,
                order.matching,
            );
        });
        return forkJoin(htmlObservables).pipe(
            switchMap((htmls: string[]) =>
                this.printSheetService.renderLazadaShippingLabelBase64(htmls),
            ),
        );
    }

    private renderTiktokLabelsPdf(
        orders: UnifiedOrder[],
    ): Observable<(string | null)[]> {
        const labelObservables = _flatten(
            _map(orders, (order: UnifiedOrder) => {
                const service =
                    this.getStoreInjector(order).get(TiktokOpenService);
                const packageIds: string[] = _map(
                    _get(order.raw, 'packages') || [],
                    'id',
                );
                return _map(packageIds, (packageId: string) =>
                    service.getPackageShippingDocuments(packageId).pipe(
                        switchMap((shippingDocument) => {
                            const docUrl = _get(shippingDocument, 'doc_url');
                            if (!docUrl) {
                                return throwError(
                                    new Error(
                                        `No shipping label for order ${order.orderId}`,
                                    ),
                                );
                            }
                            return this.fetchAndStampTiktokLabel(
                                service,
                                docUrl,
                                order,
                            );
                        }),
                    ),
                );
            }),
        );
        if (_isEmpty(labelObservables)) {
            return of([]);
        }
        return forkJoin(labelObservables);
    }

    private fetchAndStampTiktokLabel(
        service: TiktokOpenService,
        docUrl: string,
        order: UnifiedOrder,
    ): Observable<string | null> {
        // TikTok's CDN blocks cross-origin browser fetches, so the PDF is
        // downloaded through the tiktok-delegate Lambda instead.
        return service.downloadShippingDocument(docUrl).pipe(
            switchMap((pdfBase64: string) =>
                this.printSheetService.stampTiktokMatchingNumber(
                    pdfBase64,
                    order.matching,
                ),
            ),
            catchError((error) => {
                // Until the delegate supports downloadUrl (or if the
                // download fails), fall back to opening the label in its
                // own tab (unstamped) so printing still goes through.
                console.error(error);
                window.open(docUrl, '_blank');
                this.notifyError(
                    new Error(
                        'Label opened in a separate tab without a matching number',
                    ),
                    `TikTok ${order.orderId}`,
                );
                return of(null);
            }),
        );
    }

    private fetchLastPrints(): Observable<LastPrintResult[]> {
        const platforms: string[] = _uniq(
            _map(
                this.storeContexts,
                (context: StoreContext) => context.store.platform,
            ),
        );
        if (_isEmpty(platforms)) {
            return of([]);
        }
        return forkJoin(
            _map(platforms, (platform: string) =>
                this.lastprintTimeService
                    .get(platform as LastPrintPlatform)
                    .pipe(
                        catchError((error) => {
                            console.error(error);
                            return of([] as LastPrintResult[]);
                        }),
                    ),
            ),
        ).pipe(
            map((lastPrintLists: LastPrintResult[][]) =>
                _flatten(lastPrintLists),
            ),
        );
    }

    private fillLastPrintData(
        orders: UnifiedOrder[],
        lastPrints: LastPrintResult[],
    ) {
        const lastPrintsByOrderSn = _keyBy(lastPrints, 'orderSn');
        _each(orders, (order: UnifiedOrder) => {
            order.lastPrintMoment = _get(
                lastPrintsByOrderSn[order.orderId],
                'time',
            );
        });
    }

    private saveLastPrints(orders: UnifiedOrder[]): Observable<unknown> {
        const platformGroups = _groupBy(orders, 'platform');
        const putObservables = _map(
            platformGroups,
            (platformOrders: UnifiedOrder[], platform: string) =>
                this.lastprintTimeService.put(
                    platform as LastPrintPlatform,
                    _map(platformOrders, 'orderId'),
                ),
        );
        return _isEmpty(putObservables) ? of([]) : forkJoin(putObservables);
    }

    private getLazadaFullName(addressObj: any): string {
        return `${_get(addressObj, 'first_name') || ''} ${
            _get(addressObj, 'last_name') || ''
        }`.trim();
    }

    private getLazadaFullAddress(addressObj: any): string {
        const addressFields: string[] = [
            'address1',
            'address2',
            'address3',
            'address4',
            'address5',
            'country',
        ];
        let fullAddressString = '';
        _each(addressFields, (addressField) => {
            const address = _trim(_get(addressObj, addressField));
            if (!_isEmpty(address)) {
                fullAddressString = _isEmpty(fullAddressString)
                    ? address
                    : `${fullAddressString}, ${address}`;
            }
        });
        return fullAddressString;
    }

    // Groups per-unit item entries (Lazada order_items / TikTok line_items
    // contain one entry per unit) into one row per SKU.
    private groupUnitItems(
        rawItems: any[],
        skuField: string,
        getName: (item: any) => string,
        getPrice: (item: any) => any,
        getVariation: (item: any) => string,
    ): UnifiedOrderItem[] {
        let itemNo = 0;
        const groupedItems = _groupBy(rawItems, skuField);
        return _map(groupedItems, (items: any[], sku: string) => {
            const candidateItem = items[0];
            const quantity = items.length;
            const unitPrice = this.utilService.localStringToNumber(
                getPrice(candidateItem),
            );
            return {
                itemNo: ++itemNo,
                description: PrintSheetUtils.constructItemDescription(
                    sku,
                    getName(candidateItem),
                    quantity,
                    getVariation(candidateItem),
                ),
                quantity,
                unitPrice,
                amount: unitPrice * quantity,
            };
        });
    }

    // TikTok masks recipient name/address with long runs of asterisks;
    // collapse any run of 3+ asterisks down to exactly 3 to keep it tidy.
    private shortenAsteriskMask(value: string): string {
        return value.replace(/\*{3,}/g, '***');
    }

    private toMatchingNumber(orderId: unknown): string {
        return orderId
            ? PrintSheetUtils.generateMatchingNumber(`${orderId}`)
            : '';
    }

    private getTabByStatus(orderStatus: UnifiedOrderStatus): OrderStatusTab {
        return _find(this.orderStatuses, { key: orderStatus })!;
    }

    private notifySuccess(message: string): void {
        this.notificationService.pushMessage({
            displayTime: 6000,
            message,
            messageType: MessageType.success,
        });
    }

    private notifyError(error: unknown, label?: string): void {
        console.error(error);
        const errorMessage = this.utilService.errorToString(error);
        this.notificationService.pushMessage({
            displayTime: 6000,
            message: label ? `${label}: ${errorMessage}` : errorMessage,
            messageType: MessageType.error,
        });
    }

    private runPrintTask(
        printObservable: Observable<unknown>,
        successMessage: string,
    ): void {
        this.dataGridBeginLoading();
        printObservable.subscribe(
            () => {
                this.notifySuccess(successMessage);
                // The loading indicator stays up through the reload
                // triggered here, which ends it.
                this.updateOrderSubject.next(undefined);
            },
            (error) => {
                this.dataGridEndLoading();
                this.notifyError(error);
            },
        );
    }

    // The grid's own load panel is disabled in the template: once created it
    // would also flash on every internal reload (selection refresh,
    // enrichment repaint, the receipt poll), which is what made the indicator
    // blink. The standalone dx-load-panel bound to isDataLoading is the only
    // indicator — one show per loading window, one hide.
    // beginCustomLoading is still called so the grid holds back its
    // "No data" text while orders load.
    private dataGridBeginLoading(): void {
        this.dataGrid.instance.beginCustomLoading('');
        this.isDataLoading = true;
        this.cdref.detectChanges();
    }

    private dataGridEndLoading(): void {
        this.dataGrid.instance.endCustomLoading();
        this.isDataLoading = false;
        this.cdref.detectChanges();
    }
}
